/**
 * This is used as both adding and editing dailylogs along with the tasks
 *updateLog: this is to update the state via redux as and when we type into the textboxes so that the new letters are displayed on the textboxes
otherwise textboxes will appear locked. The updated values of the fields get captured in the "log" prop. 

 */

import React, { Component } from "react";
import moment from 'moment';
import { Table, Navbar, NavbarBrand, Nav, NavbarToggler, Collapse, NavItem, Row, Col, Button, Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input, Alert, Badge } from 'reactstrap';
import { NavLink } from 'react-router-dom';
import { Control, LocalForm, Errors } from 'react-redux-form';
import { v4 as uuid } from 'uuid';
import { connect } from 'react-redux';
import { getOpenAIText, postTaskCommon, updateTaskCommon, fetchComments, postComment, postTask, fetchTasks, addNewTask, updateLog, saveUpdatesToLog, updateTask, updateTaskStatus } from '../redux/ActionCreators';
import { ConfigureStore, store } from "../redux/ConfigureStore";
import DatePicker from 'react-datepicker';
import AddTaskCommon from './AddTaskCommon';
import QuestionAnsComp from './QuestionAnsComp'; // Adjust the path as needed
import DiagramEditor from './DiagramEditor';
import QuestionDiagram from './QuestionDiagram';
import { baseUrl } from '../Shared/baseURL';
import { authHeaders } from '../logic/authHeaders';
import { getActiveCourseId, setActiveCourseId } from '../logic/courseContext';
import {
    buildQuestionDiagramPayload,
    createEmptyExcalidrawScene,
    DIAGRAM_FORMATS,
    normalizeQuestionDiagram,
} from '../Shared/questionDiagramUtils';
import { ORDERED_OPTION_KEYS } from '../Shared/questionOptions';



import "react-datepicker/dist/react-datepicker.css";
import 'bootstrap/dist/css/bootstrap.min.css';
import "../Shared/styles.css";

import isEqual from 'lodash/isEqual';
import { isFunction } from "lodash";

// Predefined tasks constant
const PREDEFINED_TASKS = (subject, topic, subtopic, logId) => [
    { task: `Read ${topic}`, desc: `Read ${topic}`, subject, topic, subtopic, priority: "", dailylogid: logId },
    { task: `Take detailed notes on ${topic}`, desc: `Focus on key concepts, important dates, and definitions`, subject, topic, subtopic, priority: "", dailylogid: logId },
    { task: `Answer end-of-chapter questions on ${topic}`, desc: `Complete all review questions to ensure understanding of the material`, subject, topic, subtopic, priority: "", dailylogid: logId },
    { task: `Complete additional practice questions on ${topic}`, desc: `Solve extra problems to strengthen your grasp of the topic`, subject, topic, subtopic, priority: "", dailylogid: logId },
    { task: `Create flashcards for ${topic}`, desc: `Develop a set of flashcards focusing on key terms and concepts of ${topic}`, subject, topic, subtopic, priority: "", dailylogid: logId }
];


// Functional component to render tasks
function RenderTasks({ task, onClick, onTaskClick }) {
    // Format the due date if it exists
    const dtDue = task.due ? new Intl.DateTimeFormat('en-AU', {
        weekday: 'short', year: 'numeric', month: 'short', day: '2-digit'
    }).format(new Date(Date.parse(task.due))) : "";

    // Handle task click to ensure the correct task ID is passed
    const handleTaskClick = () => onTaskClick(task._id);

    // Determine if the task is done
    const isTaskDone = task.status === "done";

    // Render the task row with appropriate checkbox status and due date
    return (
        <tr onClick={(event) => onClick(event, task._id)}>
            <td>
                <FormGroup check>
                    <Input
                        id="taskStatus"
                        type="checkbox"
                        onChange={(event) => onClick(event, task._id)}
                        value={task._id}
                        checked={isTaskDone} // Set checkbox status based on task completion
                    />
                    {' '}
                    <Label check>{task.task}</Label> {/* Display the task name */}
                </FormGroup>
            </td>
            <td>{dtDue}</td> {/* Display the formatted due date */}
        </tr>
    );
}

const required = (val) => val && val.length;
const maxLength = (len) => (val) => !(val) || (val.length <= len);
const minLength = (len) => (val) => (val) && (val.length >= len);
const isNumber = (val) => !isNaN(Number(val));
const validEmail = (val) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(val);

const extractDiagramState = (question = {}) => buildQuestionDiagramPayload(question);

// Generated authoring payloads use snake_case while saved questions use camelCase.
const buildGeneratedDiagramFields = (question = {}) => {
    const normalizedDiagram = normalizeQuestionDiagram(question);
    return {
        diagram_format: normalizedDiagram.diagramFormat,
        diagram_scene: normalizedDiagram.diagramScene,
        diagram_code: normalizedDiagram.diagramFormat === DIAGRAM_FORMATS.SVG ? normalizedDiagram.diagramCode : null,
    };
};

const QUESTION_PURPOSE_OPTIONS = [
    { value: 'exam', label: 'Exam-style question', help: 'Primary question used in tests and normal practice. A wrong answer can trigger parent drill suggestions.' },
    { value: 'remediation_drill', label: 'Drill question', help: 'Follow-up practice assigned after the student misses an exam-style question.' },
    { value: 'challenge', label: 'Challenge question', help: 'Extension question kept out of the normal exam-style pool.' },
];

const normalizeQuestionPurpose = (value) => {
    const purpose = String(value || '').trim();
    return QUESTION_PURPOSE_OPTIONS.some((option) => option.value === purpose) ? purpose : 'exam';
};

const getQuestionPurposeOption = (value) => QUESTION_PURPOSE_OPTIONS.find((option) => option.value === normalizeQuestionPurpose(value)) || QUESTION_PURPOSE_OPTIONS[0];
const normalizeTargetSubskills = (value) => {
    if (Array.isArray(value)) {
        return value.map((entry) => String(entry || '').trim()).filter(Boolean);
    }
    return [];
};

//state is passed as parameter. this state is the current state of the redux store
//Each field in the object will become a prop for your actual component
//The values in the fields will be used to determine if your component needs to re-render
const mapStateToProps = state => {
    //alert(state.dishes);
    return {
        tasks: state.tasks,
        log: state.log,
        openAIResponse: state.openAIResponse

    }
}

/*
allows you to specify which actions your component might need to dispatch
The mapDispatchToProps function will be called with dispatch as the first argument. 
You will normally make use of this by returning new functions that call dispatch() inside themselves, 
and either pass in a plain action object directly or pass in the result of an action creator
Each field in the object will become a separate prop for your own component, 
and the value should normally be a function that dispatches an action when called 
The return of the mapDispatchToProps function will be merged to your connected component as props. 
You may call them directly to dispatch its action.
*/
const mapDispatchToProps = (dispatch) => ({

    updateTaskCommon: (tasks, taskId, onSuccessCallback) => { dispatch(updateTaskCommon(tasks, taskId, onSuccessCallback)) },
    postTaskCommon: (tasks, onSuccessCallback) => { dispatch(postTaskCommon(tasks, onSuccessCallback)) },
    fetchTasks: (id) => { dispatch(fetchTasks(id)) },
    addNewTask: (unique_id, dailylogid, tasks, task, duedate) => { dispatch(addNewTask(unique_id, dailylogid, tasks, task, duedate)) },
    updateLog: (id, subject, topic, subtopic, comment) => { dispatch(updateLog(id, subject, topic, subtopic, comment)) },
    saveUpdatesToLog: (id, subject, topic, subtopic, comment, noOfTasks, noOfDoneTasks) => { dispatch(saveUpdatesToLog(id, subject, topic, subtopic, comment, noOfTasks, noOfDoneTasks)) },
    updateTask: (unique_id, dailylogid, tasks, task, status) => { dispatch(updateTask(unique_id, dailylogid, tasks, task, status)) },
    updateTaskStatus: (unique_id, dailylogid, tasks, task, status) => { dispatch(updateTaskStatus(unique_id, dailylogid, tasks, task, status)) },
    getOpenAIText: (model, modelURL, prompt, onSuccessCallback) => { dispatch(getOpenAIText(model, modelURL, prompt, onSuccessCallback)) }


});

class AddEditQuestions extends Component {
    constructor(props) {
        super(props);
        // Course resolution order: props -> active course in localStorage -> explicit admin selection.
        // Multi-course: admin actions are scoped to selected courseId.
        const resolvedCourseId = this.props.courseId || getActiveCourseId() || '';
        this.state = {
            selectedSubject: this.props.preselectedSubject || '', // Set initial value from props
            selectedTopic: this.props.preselectedTopic || '',     // Set initial value from props
            selectedSubtopic: this.props.preselectedSubtopic || '', // Initialize from props
            selectedSubtopicName: this.props.preselectedSubtopicName || '',
            isFormDirty: false, // Tracks whether the form has unsaved changes
            saveSuccess: false, // Indicates if the save was successful
            isSaving: false, // Prevent duplicate saves while the current request is still in flight.
            saveError: '', // Shows a user-friendly save error inside the modal instead of only relying on alerts.
            isNavOpen: false,
            isModalOpen: false,
            disAddTask: true,
            strTask: "",
            strTaskStatus: "",
            startDate: new Date(),
            doneTasksNos: 0,
            isCommonTaskOpen: false,
            dailylogid: "",
            subject: "",
            topic: "",
            subtopic: "",
            calledfrom: "addtask",
            allTasks: "",
            taskId: "",
            comment: "",
            logid: "",
            isDailyLogSaved: false,
            predefinedTasks: [
                // Add more tasks as needed
            ],
            mode: "add", // Add mode state variable            
            selectedCourse: "",
            question: this.props.question ? this.props.question : "", // If in edit mode, prepopulate
            options: this.props.question ? this.props.question.options : { A: "", B: "", C: "", D: "" },
            correctAnswer: this.props.question ? this.props.question.correct_answer : "",
            steps: this.props.question ? this.props.question.steps : "",
            mode: "add",
            buttonText: "Save/Next", // Button text for adding or updating questions 
            subjects: [], // Array of subjects for the selected course
            topics: [], // Array of topics for the selected subject
            subtopics: [],
            skills: [], // Array of skills for the selected topic
            selectedSubjectID: '',
            courseId: resolvedCourseId,
            selectedCourseName: "",
            courses: [],  // Ensure courses is an array initially
            courseSelectionRequired: !resolvedCourseId,
            // Existing state properties...
            questionType: 'multiple-choice',  // Default question type
            questionPurpose: 'exam', // Distinguishes primary exam inventory from drill/challenge inventory.
            remediationStage: '',
            targetSubskillsText: '',
            difficultyLevel: "", //this.props.question ? this.props.question.difficulty : 1,
            subjectId: "",
            topicId: "",
            subtopicId: "",
            variationId: "",  // New field for question variation
            generateVariations: false,  // To track checkbox status
            isAddSubjectModalOpen: false, // Modal toggle state for adding subjects
            isAddTopicModalOpen: false,   // Modal toggle state for adding topics
            newSubjectName: '',           // Temporary storage for the new subject name
            newTopicName: '',             // Temporary storage for the new topic name    
            courseName: "",
            currentQuestionIndex: "",
            questions: [],
            unreviewedQuestionsState: [],
            selectedSkills: [],
            skillSearchQuery: '',
            availableSkills: [],  // Holds skills fetched from backend   
            skillSuggestions: [],  // Initialize skillSuggestions to an empty array            
            questionTitle: '',
            diagrams: false,
            diagramCode: '',
            diagramFormat: DIAGRAM_FORMATS.NONE,
            diagramScene: null,
            diagramJsonText: null,   // draft buffer for inline JSON editor (null = use live state)
            diagramJsonError: '',    // parse error message for JSON editor
            isAdvancedPanelOpen: false,  // collapsible advanced taxonomy/classification panel
            prompt: '', // State to hold the fetched prompt
            gptModel: '',
            modelUrl: '',
            difficultyAdjustment: '',  // Track difficulty adjustment ('harder' or 'simpler')
            numQuestions: 10, // default batch size
            bulkQuestionsJsonInput: '',  // Stores pasted JSON
            parseError: '',  // Error message for JSON parsing
            verified: false,
            verifiedBy: '',
            isVerifierMode: this.props.isVerifierMode || false, // Default to false
            // **New State Variables for "Mark as Incorrect"**
            isIncorrectModalOpen: false, // Controls the visibility of the Incorrect modal
            incorrectComment: '', // Stores the user's comments when marking incorrect
            basePrompt: "",
            difficultySection: "",
            guidelines: "",
            sampleQuestion: "",
            samplesteps: "",
            difficultyDefinitions: "",
            additionalInstructions: "",
            sampleCorrectAnswer: "",
            sampleAnswerOptions: "",  // New state for storing sample answer options
            // Passage context stays optional so non-passage questions keep the existing workflow and layout.
            passageSetId: '',
            questionOrderWithinPassage: '',
            passageTitle: '',
            passageText: '',
            passageSkillsText: '',
            isEditMode: false, // new flag for toggling edit mode
            newCommentText: '',           // Holds new comment text input
            editingCommentId: null,       // Holds the _id of the comment currently being edited
            editingCommentText: '',       // Holds the edited text of the comment
            forceComment: false,
            isDiagramModalOpen: false,
            lastSavedQuestionSnapshot: null, // Tracks the last persisted version so Cancel can truly discard local edits.
            copyStatus: '', // Temporarily replaces the Copy button text after clipboard actions.
   

        }
        // Bind the new toggle functions:
        this.toggleEditMode = this.toggleEditMode.bind(this);
        this.handleSaveEdits = this.handleSaveEdits.bind(this);
        this.handleCancelEdits = this.handleCancelEdits.bind(this);
        this.toggleNav = this.toggleNav.bind(this);
        this.toggleModal = this.toggleModal.bind(this);
        this.handleLogin = this.handleLogin.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleUpdateLog = this.handleUpdateLog.bind(this);
        this.handleAddTask = this.handleAddTask.bind(this);
        this.handleDateChange = this.handleDateChange.bind(this);
        this.handleTaskClick = this.handleTaskClick.bind(this);
        this.handleAddPredefinedTask = this.handleAddPredefinedTask.bind(this);
        this.renderTasks = this.renderTasks.bind(this);
        this.handleSubjectChange = this.handleSubjectChange.bind(this);
        this.handleTopicChange = this.handleTopicChange.bind(this);
        this.handleSkillsChange = this.handleSkillsChange.bind(this);
        this.fetchQuestionFromOpenAI = this.fetchQuestionFromOpenAI.bind(this);
        this.handleQuestionDataChange = this.handleQuestionDataChange.bind(this);  // bind the method
        this.handleCourseChange = this.handleCourseChange.bind(this); // Bind the method
        this.handleBulkSaveFromJson = this.handleBulkSaveFromJson.bind(this);
        this.handlePrevious = this.handlePrevious.bind(this);
        this.handleNext = this.handleNext.bind(this);
        // **Bind the new methods**
        this.toggleIncorrectModal = this.toggleIncorrectModal.bind(this);
        this.handleMarkAsIncorrect = this.handleMarkAsIncorrect.bind(this);

        this.handleSampleQuestionChange = this.handleSampleQuestionChange.bind(this);
        this.handleCorrectAnswerChange = this.handleCorrectAnswerChange.bind(this);
        this.handleStepsChange = this.handleStepsChange.bind(this);
        this.handleSampleAnsOptionsChange = this.handleSampleAnsOptionsChange.bind(this);
        this.toggleDiagramModal = this.toggleDiagramModal.bind(this);
        this.handleOpenDiagramEditor = this.handleOpenDiagramEditor.bind(this);
        this.handleSaveDiagramFromModal = this.handleSaveDiagramFromModal.bind(this);
        this.commentInputRef = React.createRef();
        this.handleAccept = this.handleAccept.bind(this);
        this.handleDeleteCurrentQuestion = this.handleDeleteCurrentQuestion.bind(this);
        this.copyStatusTimer = null; // Keeps copy feedback self-clearing without showing a blocking dialog.
   


    }

    cloneJsonValue = (value) => JSON.parse(JSON.stringify(value));

    // Translate low-level save failures into messages that make sense to question reviewers/authors.
    buildQuestionSaveErrorMessage = (error) => {
        const rawMessage = String(error?.message || '').trim();
        const normalizedMessage = rawMessage.toLowerCase();

        if (normalizedMessage.includes('difficulty')) {
            return 'We could not save this question because the difficulty level is invalid. Choose a value between 1 and 5 and try again.';
        }

        if (normalizedMessage.includes('diagram')) {
            return 'We could not save the diagram changes. Please reopen the diagram editor, confirm the diagram looks correct, and try again.';
        }

        if (rawMessage) {
            return `We could not save your changes. ${rawMessage}`;
        }

        return 'We could not save your changes. Your edits are still open in this window, so you can fix the issue and try again.';
    };

    // Review actions should only run after edits are saved, otherwise verification can use stale data.
    ensureNoPendingEdits = (actionLabel) => {
        if (!this.state.isFormDirty) {
            return true;
        }

        this.setState({
            saveError: `Please save or discard your edits before you ${actionLabel}.`
        });
        return false;
    };

    // Reset the modal back to the last persisted question state when the user discards local edits.
    restoreEditorFromQuestion = (question) => {
        const restoredQuestion = question || {};
        const restoredDiagramState = extractDiagramState(restoredQuestion);
        const restoredPassageContext = this.getPassageContextFromQuestion(restoredQuestion);
        const restoredQuestionIndex = this.state.currentQuestionIndex;
        const restoredQuestions = Array.isArray(this.state.questions) ? [...this.state.questions] : [];

        if (restoredQuestions[restoredQuestionIndex]) {
            restoredQuestions[restoredQuestionIndex] = this.cloneJsonValue(restoredQuestion);
        }

        this.setState({
            question: restoredQuestion.question || '',
            options: restoredQuestion.options || { A: "", B: "", C: "", D: "" },
            correctAnswer: this.resolveCorrectAnswerValue(restoredQuestion),
            steps: restoredQuestion.steps || '',
            questionTitle: restoredQuestion.question_title || '',
            variationId: restoredQuestion.variationId || this.state.variationId,
            questionType: restoredQuestion.questionType || this.state.questionType,
            difficultyLevel: restoredQuestion.difficulty || '',
            courseId: this.normalizeId(restoredQuestion.courseId || this.state.courseId),
            subjectId: this.normalizeId(restoredQuestion.subjectId || this.state.subjectId),
            topicId: this.normalizeId(restoredQuestion.topicId || this.state.topicId),
            subtopicId: this.normalizeId(restoredQuestion.subtopicId || ''),
            selectedSubtopic: this.normalizeId(restoredQuestion.subtopicId || ''),
            selectedSkills: Array.isArray(restoredQuestion.skills) ? restoredQuestion.skills : [],
            verified: restoredQuestion.verified || false,
            verifiedBy: restoredQuestion?.verifiedBy?.email || restoredQuestion?.verifiedBy || null,
            questionPurpose: normalizeQuestionPurpose(restoredQuestion.questionPurpose),
            remediationStage: restoredQuestion.remediationStage || '',
            targetSubskillsText: Array.isArray(restoredQuestion.targetSubskills) ? restoredQuestion.targetSubskills.join('\n') : '',
            questions: restoredQuestions,
            ...restoredDiagramState,
            passageSetId: this.normalizeId(restoredQuestion?.passageSetId || restoredPassageContext?.passageSetId),
            questionOrderWithinPassage: restoredQuestion?.questionOrderWithinPassage || '',
            passageTitle: restoredPassageContext?.passageTitle || '',
            passageText: restoredPassageContext?.passageText || '',
            passageSkillsText: Array.isArray(restoredPassageContext?.passageSkills) ? restoredPassageContext.passageSkills.join('\n') : '',
            isDiagramModalOpen: false,
            isFormDirty: false,
            saveError: '',
            saveSuccess: false,
        });
    };

    // Skill selections can be stored either as populated objects or raw ObjectId strings after a save round-trip.
    getSelectedSkillIds = () => this.state.selectedSkills
        .map((skill) => skill?._id || skill)
        .filter(Boolean);

    // Normalize mixed id shapes (string/object) so dropdown matching works consistently.
    normalizeId = (value) => {
        if (!value) return '';
        if (typeof value === 'object') {
            return String(value._id || value.id || '');
        }
        return String(value);
    };

    // Re-apply subject/topic/subtopic selection once hierarchy data is available.
    syncSelectionFromIds = (subjectIdValue, topicIdValue, subtopicIdValue) => {
        const subjectId = this.normalizeId(subjectIdValue);
        const topicId = this.normalizeId(topicIdValue);
        const subtopicId = this.normalizeId(subtopicIdValue);

        if (!subjectId) {
            return;
        }

        this.handleSubjectChange(subjectId, () => {
            if (topicId) {
                this.handleTopicChange(topicId, () => {
                    if (subtopicId) {
                        this.handleSubTopicChange(subtopicId);
                    }
                });
            }
        });
    };

    // Submit a new comment
    handleSubmitComment = async () => {
        const { newCommentText, questions, currentQuestionIndex } = this.state;
        if (!newCommentText.trim()) return;
        const selectedQuestion = questions[currentQuestionIndex];
        const token = localStorage.getItem('studyapp.com');
        try {
            const response = await fetch(`${baseUrl}questions/${selectedQuestion._id}/comments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ text: newCommentText })
            });
            const updatedQuestion = await response.json();
            // Update the current question with new comments
            const updatedQuestions = [...questions];
            updatedQuestions[currentQuestionIndex] = updatedQuestion;
            //alert("udatedQuestions comments " + JSON.stringify(updatedQuestion.comments))
            this.setState({ questions: updatedQuestions, newCommentText: '', forceComment: false }, () => {
                if (this.props.onUpdateQuestions) {
                    this.props.onUpdateQuestions(updatedQuestions, currentQuestionIndex);
                }
            });
        } catch (error) {
            console.error('Error adding comment:', error);
        }
    };

    // Initiate editing for a comment
    handleEditComment = (comment) => {
        this.setState({ editingCommentId: comment._id, editingCommentText: comment.text });
    };

    // Save the edited comment
    handleSaveEditedComment = async () => {
        const { editingCommentId, editingCommentText, questions, currentQuestionIndex } = this.state;
        if (!editingCommentText.trim()) return;
        const selectedQuestion = questions[currentQuestionIndex];
        const token = localStorage.getItem('studyapp.com');
        try {
            const response = await fetch(`${baseUrl}questions/${selectedQuestion._id}/comments/${editingCommentId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ text: editingCommentText })
            });
            const updatedQuestion = await response.json();
            const updatedQuestions = [...questions];
            updatedQuestions[currentQuestionIndex] = updatedQuestion;
            this.setState({ questions: updatedQuestions, editingCommentId: null, editingCommentText: '' });
        } catch (error) {
            console.error('Error editing comment:', error);
        }
    };

    // Delete a comment
    handleDeleteComment = async (comment) => {
        if (!window.confirm("Are you sure you want to delete this comment?")) return;
        const { questions, currentQuestionIndex } = this.state;
        const selectedQuestion = questions[currentQuestionIndex];
        const token = localStorage.getItem('studyapp.com');
        try {
            const response = await fetch(`${baseUrl}questions/${selectedQuestion._id}/comments/${comment._id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const updatedQuestion = await response.json();
            const updatedQuestions = [...questions];
            updatedQuestions[currentQuestionIndex] = updatedQuestion;
            this.setState({ questions: updatedQuestions });
        } catch (error) {
            console.error('Error deleting comment:', error);
        }
    };



    handleSaveEdits() {
        return this.handleUpdateLog(undefined, { exitEditMode: true });
    }

    handleCancelEdits() {
        if (this.state.isFormDirty) {
            const confirmDiscard = window.confirm(
                "Discard your unsaved changes and restore the last saved version of this question?"
            );
            if (!confirmDiscard) {
                return;
            }
        }

        const fallbackQuestion = Array.isArray(this.props.question)
            ? this.props.question[this.state.currentQuestionIndex]
            : null;
        const restoredQuestion = this.state.lastSavedQuestionSnapshot || fallbackQuestion;
        this.restoreEditorFromQuestion(restoredQuestion);
        this.setState({ isEditMode: false });
    }

    toggleEditMode() {
        // Simply flip the isEditMode flag
        this.setState(prevState => ({ isEditMode: !prevState.isEditMode }));
    }

    toggleDiagramModal() {
        this.setState((prevState) => ({ isDiagramModalOpen: !prevState.isDiagramModalOpen }));
    }

    handleOpenDiagramEditor() {
        const currentDiagram = normalizeQuestionDiagram({
            diagramFormat: this.state.diagramFormat,
            diagramCode: this.state.diagramCode,
            diagramScene: this.state.diagramScene,
        });

        if (currentDiagram.isSvg) {
            // Legacy SVG rows remain preview-only because we do not auto-convert them into editable scenes.
            return;
        }

        // Opening the editor should not mark the question dirty until the scene actually changes.
        this.setState({
            isDiagramModalOpen: true,
            saveError: '',
        });
    }

    handleSaveDiagramFromModal(updatedScene) {
        const nextDiagramPayload = buildQuestionDiagramPayload({
            diagramFormat: DIAGRAM_FORMATS.EXCALIDRAW,
            diagramCode: '',
            diagramScene: updatedScene,
        });
        const currentDiagramPayload = buildQuestionDiagramPayload({
            diagramFormat: this.state.diagramFormat,
            diagramCode: this.state.diagramCode,
            diagramScene: this.state.diagramScene,
        });
        const hasDiagramChanged = !isEqual(currentDiagramPayload, nextDiagramPayload);

        this.setState((prevState) => {
            if (!hasDiagramChanged) {
                return {
                    isDiagramModalOpen: false,
                };
            }

            const updatedQuestions = Array.isArray(prevState.questions) ? [...prevState.questions] : [];
            if (updatedQuestions[prevState.currentQuestionIndex]) {
                updatedQuestions[prevState.currentQuestionIndex] = {
                    ...updatedQuestions[prevState.currentQuestionIndex],
                    ...nextDiagramPayload,
                };
            }

            return {
                ...nextDiagramPayload,
                questions: updatedQuestions,
                isDiagramModalOpen: false,
                isFormDirty: true,
                saveError: '',
            };
        });
    }

    getPassageContextFromQuestion = (question = {}) => {
        const snapshot = question?.sharedStimulusSnapshot;
        if (snapshot?.passageText) {
            return {
                passageSetId: question?.passageSetId || snapshot?.passageSetId || '',
                passageTitle: snapshot?.passageTitle || '',
                passageText: snapshot?.passageText || '',
                passageSkills: Array.isArray(snapshot?.passageSkills) ? snapshot.passageSkills : [],
            };
        }

        const passageSet = question?.passageSet;
        if (passageSet?.passageText) {
            return {
                passageSetId: question?.passageSetId || passageSet?._id || '',
                passageTitle: passageSet?.passageTitle || '',
                passageText: passageSet?.passageText || '',
                passageSkills: Array.isArray(passageSet?.passageSkills) ? passageSet.passageSkills : [],
            };
        }

        return null;
    };

    resolveCorrectAnswerValue = (question = {}) => {
        const options = question?.options || this.state.options || {};
        const rawAnswer = question?.correct_answer ?? question?.correctAnswer ?? this.state.correctAnswer ?? '';
        const normalizedAnswer = String(rawAnswer == null ? '' : rawAnswer).trim();
        if (!normalizedAnswer) {
            return '';
        }
        if (options && Object.prototype.hasOwnProperty.call(options, normalizedAnswer)) {
            return options[normalizedAnswer];
        }
        return normalizedAnswer;
    };

    getCorrectAnswerDiagnostics = (question = {}) => {
        const options = question?.options || this.state.options || {};
        const rawAnswer = String(question?.correct_answer ?? question?.correctAnswer ?? this.state.correctAnswer ?? '').trim();
        const resolvedAnswer = this.resolveCorrectAnswerValue(question);
        const optionValues = Object.values(options).map((value) => String(value == null ? '' : value).trim());
        const matchesOption = optionValues.includes(String(resolvedAnswer || '').trim());

        return {
            rawAnswer,
            resolvedAnswer,
            matchesOption,
        };
    };

    parseTargetSubskillsFromState = () => normalizeTargetSubskills((this.state.targetSubskillsText || '').split('\n'));

    handlePurposeMetadataChange = (field, value) => {
        this.setState({ [field]: value, isFormDirty: true, saveError: '' });
    };

    buildPassageSnapshotFromState = () => {
        if (!this.state.passageText) {
            return null;
        }
        return {
            passageTitle: this.state.passageTitle,
            passageText: this.state.passageText,
            passageSkills: this.state.passageSkillsText
                ? this.state.passageSkillsText.split('\n').map((entry) => entry.trim()).filter(Boolean)
                : [],
        };
    };

    handlePassageFieldChange = (field, value) => {
        const updatedQuestions = Array.isArray(this.state.questions) ? [...this.state.questions] : [];
        const currentIndex = this.state.currentQuestionIndex;
        const currentQuestion = updatedQuestions[currentIndex] || {};
        const nextPassageTitle = field === 'passageTitle' ? value : this.state.passageTitle;
        const nextPassageText = field === 'passageText' ? value : this.state.passageText;
        const nextPassageSkills = field === 'passageSkillsText'
            ? value.split('\n').map((entry) => entry.trim()).filter(Boolean)
            : (this.state.passageSkillsText || '').split('\n').map((entry) => entry.trim()).filter(Boolean);
        const nextSharedStimulusSnapshot = {
            ...(currentQuestion.sharedStimulusSnapshot || this.buildPassageSnapshotFromState() || {}),
            passageTitle: nextPassageTitle,
            passageText: nextPassageText,
            passageSkills: nextPassageSkills,
        };

        if (updatedQuestions[currentIndex]) {
            updatedQuestions[currentIndex] = {
                ...updatedQuestions[currentIndex],
                sharedStimulusSnapshot: nextSharedStimulusSnapshot,
            };
        }

        this.setState({
            [field]: value,
            questions: updatedQuestions,
            isFormDirty: true,
            saveError: '',
        });
    };

    buildQuestionCommentData = (question = {}) => ({
        id: null,
        object: null,
        created: null,
        model: null,
        choices: [
            {
                index: 0,
                message: {
                    role: "assistant",
                    content: JSON.stringify({
                        question: question.question,
                        options: question.options,
                        correct_answer: this.resolveCorrectAnswerValue(question),
                        steps: Array.isArray(question.steps) ? question.steps : (question.steps ? question.steps.split('\n') : []),
                        ...buildGeneratedDiagramFields(question)
                    })
                },
                refusal: null
            }
        ],
        usage: null,
        system_fingerprint: null
    });

    // Build a single review-friendly text block for external LLM review (e.g., ChatGPT paste).
    buildQuestionReviewPayload = () => {
        const currentQuestion = this.state.questions?.[this.state.currentQuestionIndex] || {};
        const questionTitle = currentQuestion.question_title || this.state.questionTitle || '';
        const questionText = currentQuestion.question || this.state.question || '';
        const correctAnswer = this.resolveCorrectAnswerValue(currentQuestion) || this.state.correctAnswer || '';
        const stepsRaw = currentQuestion.steps || this.state.steps || '';
        const passageContext = this.getPassageContextFromQuestion(currentQuestion);

        // Normalize option payloads across object/array/string backend shapes.
        const optionsRaw = currentQuestion.options || this.state.options || {};
        let optionsText = '';
        if (Array.isArray(optionsRaw)) {
            optionsText = optionsRaw
                .map((value, index) => `${String.fromCharCode(65 + index)}: ${value}`)
                .join('\n');
        } else if (typeof optionsRaw === 'object' && optionsRaw !== null) {
            optionsText = Object.entries(optionsRaw)
                .map(([key, value]) => `${key}: ${value}`)
                .join('\n');
        } else {
            optionsText = String(optionsRaw || '');
        }

        // Steps can arrive as string or array; normalize to multiline text for easy paste.
        const stepsText = Array.isArray(stepsRaw) ? stepsRaw.join('\n') : String(stepsRaw || '');

        return [
            passageContext?.passageTitle ? `Shared Passage Title:\n${passageContext.passageTitle}` : '',
            passageContext?.passageText ? `Shared Passage:\n${passageContext.passageText}` : '',
            questionTitle ? `Question Title:\n${questionTitle}` : '',
            `Question:\n${questionText}`,
            `Answer Options:\n${optionsText}`,
            `Correct Answer:\n${correctAnswer}`,
            `Steps:\n${stepsText}`
        ].filter(Boolean).join('\n\n');
    };

    // Show short-lived copy feedback inline on the button so reviewers do not need to dismiss a dialog.
    setCopyStatus = (copyStatus) => {
        if (this.copyStatusTimer) {
            clearTimeout(this.copyStatusTimer);
        }

        this.setState({ copyStatus });
        this.copyStatusTimer = setTimeout(() => {
            this.setState({ copyStatus: '' });
            this.copyStatusTimer = null;
        }, 1800);
    };

    // Copy the current verify-screen question details as one text payload for review workflows.
    copyCurrentQuestionForReview = async () => {
        const reviewPayload = this.buildQuestionReviewPayload();
        if (!reviewPayload.trim()) {
            this.setCopyStatus('Nothing to copy');
            return;
        }

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(reviewPayload);
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = reviewPayload;
                textArea.style.position = 'fixed';
                textArea.style.opacity = '0';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
            this.setCopyStatus('Question copied');
        } catch (error) {
            console.error('Failed to copy question details:', error);
            this.setCopyStatus('Copy failed');
        }
    };


    // Handler for Sample Question Change
    handleSampleQuestionChange = (e) => {
        this.setState({ sampleQuestion: e.target.value }, () => {
            this.fetchPrompt();
        });
    };



    handleSampleAnsOptionsChange = (e) => {
        this.setState({ sampleAnswerOptions: e.target.value }, () => {
            this.fetchPrompt();
        });
    };

    // Handler for Correct Answer Change
    handleCorrectAnswerChange = (e) => {
        this.setState({ sampleCorrectAnswer: e.target.value }, () => {
            this.fetchPrompt();
        });
    };

    // Handler for Steps Change
    handleStepsChange = (e) => {
        this.setState({ samplesteps: e.target.value }, () => {
            this.fetchPrompt();
        });
    };

    // **New Method: Toggle Incorrect Modal**
    toggleIncorrectModal() {
        this.setState(prevState => ({
            isIncorrectModalOpen: !prevState.isIncorrectModalOpen,
            incorrectComment: '', // Reset comments when toggling
        }));
    }
    // **New Method: Handle Mark as Incorrect**
    handleMarkAsIncorrect(event) {
        event.preventDefault();

        if (!this.ensureNoPendingEdits('mark the question as incorrect')) {
            return;
        }

        const selectedQuestion = this.state.questions[this.state.currentQuestionIndex];

        if (!selectedQuestion) {
            alert("No question selected.");
            return;
        }
        if (selectedQuestion.workflow === "accepted") {
            alert("This question has been accepted and cannot be modified.");
            return;
        }

        const token = localStorage.getItem('studyapp.com'); // Assuming JWT token

        fetch(`${baseUrl}questions/${selectedQuestion._id}/incorrect`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                comments: this.state.incorrectComment,
            }),
        })
            .then(async response => {
                if (!response.ok) {
                    // pull the server’s JSON error message
                    const err = await response.json();
                    alert('Daily review limit reached');
                }
                return response.json();
            })
            .then(updatedQuestion => {
                // Update the question in the state
                const updatedQuestions = [...this.state.questions];
                updatedQuestions[this.state.currentQuestionIndex] = updatedQuestion;
                updatedQuestions[this.state.currentQuestionIndex] = {
                    ...updatedQuestions[this.state.currentQuestionIndex],
                    incorrect: updatedQuestion.incorrect,
                    incorrectBy: updatedQuestion.incorrectBy,
                    incorrectBy: {
                        ...updatedQuestion.incorrectBy,
                        email: updatedQuestion.incorrectBy.email
                    },
                    comments: updatedQuestion.comments,
                };
                this.setState({
                    questions: updatedQuestions,
                    incorrectComment: '',
                    isIncorrectModalOpen: false,
                    forceComment: true,
                    saveSuccess: true
                }, () => {
                    if (this.commentInputRef.current) {
                        this.commentInputRef.current.focus();
                        if (typeof this.commentInputRef.current.setSelectionRange === 'function') {
                            this.commentInputRef.current.setSelectionRange(0, 0);
                        }
                    }
                    if (this.props.onUpdateQuestions) {
                        this.props.onUpdateQuestions(updatedQuestions);
                    }
                });
                setTimeout(() => {
                    this.setState({ saveSuccess: false });
                }, 3000);
            })
            .catch(error => {
                console.error('Error marking question as incorrect:', error);
                alert('Failed to mark question as incorrect.');
            });
    }



    handleSkillSelect = (skillName) => {
        // Ensure skillSuggestions is an array and skillName exists
        const existingSkill = Array.isArray(this.state.skillSuggestions)
            ? this.state.skillSuggestions.find(skill => skill?.name?.toLowerCase() === skillName?.toLowerCase())
            : null;

        if (!existingSkill) {
            // If the skill doesn't exist, add it to the database
            fetch(`${baseUrl}skills`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    name: skillName,
                    subjectId: this.state.subjectId,
                    topicId: this.state.topicId,
                }),
            })
                .then(response => response.json())
                .then(newSkill => {
                    // Ensure newSkill has a valid structure before updating state
                    if (newSkill && newSkill.name) {
                        this.setState(prevState => ({
                            selectedSkills: [...prevState.selectedSkills, newSkill],  // Store the entire skill object
                            skillSearchQuery: '',  // Clear the input field
                            skillSuggestions: [...prevState.skillSuggestions, newSkill],  // Add the new skill to the suggestions list
                        }));
                    }
                })
                .catch(error => console.error('Error saving new skill:', error));
        } else {
            // If the skill exists, add it directly to selectedSkills
            this.setState(prevState => ({
                selectedSkills: [...prevState.selectedSkills, existingSkill],
                skillSearchQuery: '',  // Clear input field
            }));
        }
    };



    handleSkillSearchChange = (e) => {
        const searchQuery = e.target.value;
        this.setState({ skillSearchQuery: searchQuery });

        if (e.key === 'Tab') {
            e.preventDefault();  // Prevent the default tab behavior
            this.handleSkillSelect(searchQuery);
        } else {
            // Fetch skill suggestions, now passing subjectId and topicId as well
            const { subjectId, topicId } = this.state; // Assuming subjectId and topicId are set in state

            fetch(`${baseUrl}skills/search?query=${searchQuery}&subjectId=${subjectId}&topicId=${topicId}`, { headers: authHeaders() })
                .then(response => response.json())
                .then(suggestions => {
                    this.setState({ availableSkills: suggestions });
                })
                .catch(error => console.error('Error fetching skill suggestions:', error));
        }
    };


    handleVerifyQuestion = async () => {
        if (!this.ensureNoPendingEdits('mark the question as correct')) {
            return;
        }

        const { questions, currentQuestionIndex } = this.state;
        const currentQuestion = questions[currentQuestionIndex] || {};
        const effectiveOptions = currentQuestion.options || this.state.options || {};
        const effectiveCorrectAnswer = this.resolveCorrectAnswerValue(currentQuestion) || this.state.correctAnswer || '';
        const normalizedOptionValues = Object.values(effectiveOptions).map((value) => String(value == null ? '' : value).trim());

        // Validation: always check against the currently displayed question so review-mode navigation cannot drift state.
        const isValid = normalizedOptionValues.includes(String(effectiveCorrectAnswer).trim());
        if (!isValid) {
            alert("Validation failed: The correct answer does not match any of the options.");
            return; // Stop further processing if validation fails
        }

        // Validation 2: Check for duplicate options
        const uniqueOptions = new Set(normalizedOptionValues); // Set automatically removes duplicates
        // alert('uniqueOptions.size :' + uniqueOptions.size)
        // alert('options.length :' + Object.values(options).length)
        if (uniqueOptions.size !== normalizedOptionValues.length) {
            alert("Validation failed: Option values must be unique.");
            return;
        }

        if (this.state.questions[this.state.currentQuestionIndex].workflow === "accepted") {
            alert("This question has been accepted and cannot be modified.");
            return;
        }


        //const selectedQuestion = this.props.question._id;
        const selectedQuestion = this.state.questions[this.state.currentQuestionIndex]._id;

        const token = localStorage.getItem('studyapp.com');

        try {
            // Await the fetch response
            const response = await fetch(`${baseUrl}questions/${selectedQuestion}/verify`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) {
                // pull down the error message
                const err = await response.json();
                alert(err.message || 'Daily review limit reached');
                return;    // stop here
            }

            // Await the parsing of JSON data
            const updatedQuestion = await response.json();
            //            alert ("updatedQuestion " + JSON.stringify(updatedQuestion))
            // alert("Question verified successfully! " + updatedQuestion.verifiedBy.username);
            // alert("comments " + JSON.stringify(updatedQuestion.comments))
            const updatedQuestions = [...this.state.questions];
            updatedQuestions[this.state.currentQuestionIndex] = {
                ...updatedQuestions[this.state.currentQuestionIndex],
                verified: updatedQuestion.verified,
                verifiedBy: updatedQuestion.verifiedBy,
                verifiedBy: {
                    ...updatedQuestion.verifiedBy,
                    email: updatedQuestion.verifiedBy.email
                },
                incorrect: updatedQuestion.incorrect,
                incorrectBy: updatedQuestion.incorrectBy,
                comments: updatedQuestion.comments,

            };



            // Update the state with the verified status and verifier details
            this.setState({ questions: updatedQuestions, saveSuccess: true }, () => {
                if (this.props.onUpdateQuestions) {
                    this.props.onUpdateQuestions(updatedQuestions);
                }
            });
            setTimeout(() => {
                this.setState({ saveSuccess: false });
            }, 3000);
        } catch (error) {
            console.error('Error verifying question:', error);
        }
    };



    handleNext = () => {

        if (this.state.forceComment) {
            alert("Please enter and submit a comment before navigating to another question.");
            return;
        }

        if (this.state.isFormDirty) {
            const confirmClose = window.confirm(
                "You have unsaved changes. Are you sure you want to close without saving?"
            );
            if (!confirmClose) {
                return; // Prevent modal from closing
            }
        }

        const { questions, currentQuestionIndex, difficultyLevel, isVerifierMode, unreviewedQuestionsState } = this.state;
        const updatedQuestions = [...questions];
        const unreviewedQuestionsStateConstant = [...unreviewedQuestionsState];
        updatedQuestions[currentQuestionIndex] = {
            ...updatedQuestions[currentQuestionIndex],
            difficulty: difficultyLevel,
        };

        if (this.props.isReviewer) {
            // For reviewers: only navigate unreviewed questions
            //const unreviewedQuestions = questions.filter(q => !q.verifiedBy && !q.incorrectBy);
            const currentQuestion = questions[currentQuestionIndex];
            const currentUnreviewedIndex = unreviewedQuestionsStateConstant.findIndex(q => q._id === currentQuestion._id);
            //alert("currentUnreviewedIndex " + currentUnreviewedIndex)
            //alert("unreviewedQuestions.length " + unreviewedQuestionsState.length)
            if (currentUnreviewedIndex === -1 || currentUnreviewedIndex >= unreviewedQuestionsStateConstant.length - 1) {
                alert('No more unreviewed questions available');
                return;
            }
            const nextUnreviewedQuestion = unreviewedQuestionsStateConstant[currentUnreviewedIndex + 1];
            const nextIndex = questions.findIndex(q => q._id === nextUnreviewedQuestion._id);
            const nextQuestion = questions[nextIndex];
            const commentData = this.buildQuestionCommentData(nextQuestion);
            const passageContext = this.getPassageContextFromQuestion(nextQuestion);
            const nextDiagramState = extractDiagramState(nextQuestion);

            this.setState({
                question: nextQuestion.question,
                options: nextQuestion.options || { A: "", B: "", C: "", D: "" },
                currentQuestionIndex: nextIndex,
                comment: commentData,
                questionTitle: nextQuestion.question_title,
                verified: nextQuestion.verified,
                correctAnswer: this.resolveCorrectAnswerValue(nextQuestion),
                steps: nextQuestion.steps || '',
                ...nextDiagramState,
                diagramJsonText: null,
                diagramJsonError: '',
                isAdvancedPanelOpen: false,
                questions: updatedQuestions,
                difficultyLevel: updatedQuestions[nextIndex]?.difficulty || "",
                passageSetId: this.normalizeId(nextQuestion?.passageSetId || passageContext?.passageSetId),
                questionOrderWithinPassage: nextQuestion?.questionOrderWithinPassage || '',
                passageTitle: passageContext?.passageTitle || '',
                passageText: passageContext?.passageText || '',
                passageSkillsText: Array.isArray(passageContext?.passageSkills) ? passageContext.passageSkills.join('\n') : '',
                isFormDirty: false,
                saveError: '',
                isDiagramModalOpen: false,
                isEditMode: false,
                lastSavedQuestionSnapshot: nextQuestion ? this.cloneJsonValue(nextQuestion) : null,
            });
        } else {
            // For non-reviewers: use existing navigation logic (increment index normally)
            if (currentQuestionIndex < questions.length - 1) {
                const nextIndex = currentQuestionIndex + 1;
                const nextQuestion = questions[nextIndex];
                const commentData = this.buildQuestionCommentData(nextQuestion);
                const passageContext = this.getPassageContextFromQuestion(nextQuestion);
                const nextDiagramState = extractDiagramState(nextQuestion);

                this.setState({
                    question: nextQuestion.question,
                    options: nextQuestion.options || { A: "", B: "", C: "", D: "" },
                    currentQuestionIndex: nextIndex,
                    comment: commentData,
                    questionTitle: nextQuestion.question_title,
                    verified: nextQuestion.verified,
                    correctAnswer: this.resolveCorrectAnswerValue(nextQuestion),
                    steps: nextQuestion.steps || '',
                    ...nextDiagramState,
                    diagramJsonText: null,
                    diagramJsonError: '',
                    isAdvancedPanelOpen: false,
                    questions: updatedQuestions,
                    difficultyLevel: updatedQuestions[nextIndex]?.difficulty || "",
                    passageSetId: this.normalizeId(nextQuestion?.passageSetId || passageContext?.passageSetId),
                    questionOrderWithinPassage: nextQuestion?.questionOrderWithinPassage || '',
                    passageTitle: passageContext?.passageTitle || '',
                    passageText: passageContext?.passageText || '',
                    passageSkillsText: Array.isArray(passageContext?.passageSkills) ? passageContext.passageSkills.join('\n') : '',
                    isFormDirty: false,
                    saveError: '',
                    isDiagramModalOpen: false,
                    isEditMode: false,
                    lastSavedQuestionSnapshot: nextQuestion ? this.cloneJsonValue(nextQuestion) : null,
                });
            } else {
                console.warn("No more questions available.");
            }
        }
    };

    handlePrevious = () => {

        if (this.state.forceComment) {
            alert("Please enter and submit a comment before navigating to another question.");
            return;
        }
        if (this.state.isFormDirty) {
            const confirmClose = window.confirm(
                "You have unsaved changes. Are you sure you want to close without saving?"
            );
            if (!confirmClose) {
                return; // Prevent modal from closing
            }
        }

        const { questions, currentQuestionIndex, difficultyLevel, isVerifierMode, unreviewedQuestionsState } = this.state;
        const updatedQuestions = [...questions];
        const unreviewedQuestionsStateConstant = [...unreviewedQuestionsState];
        updatedQuestions[currentQuestionIndex] = {
            ...updatedQuestions[currentQuestionIndex],
            difficulty: difficultyLevel,
        };

        if (this.props.isReviewer) {
            // For reviewers: only navigate unreviewed questions
            //const unreviewedQuestions = questions.filter(q => !q.verifiedBy && !q.incorrectBy);
            const currentQuestion = questions[currentQuestionIndex];
            const currentUnreviewedIndex = unreviewedQuestionsStateConstant.findIndex(q => q._id === currentQuestion._id);
            if (currentUnreviewedIndex <= 0) {
                alert('This is the first unreviewed question');
                return;
            }
            const prevUnreviewedQuestion = unreviewedQuestionsStateConstant[currentUnreviewedIndex - 1];
            const prevIndex = questions.findIndex(q => q._id === prevUnreviewedQuestion._id);
            const previousQuestion = questions[prevIndex];
            const commentData = this.buildQuestionCommentData(previousQuestion);
            const passageContext = this.getPassageContextFromQuestion(previousQuestion);
            const previousDiagramState = extractDiagramState(previousQuestion);

            this.setState({
                question: previousQuestion.question,
                options: previousQuestion.options || { A: "", B: "", C: "", D: "" },
                currentQuestionIndex: prevIndex,
                comment: commentData,
                questionTitle: previousQuestion.question_title,
                verified: previousQuestion.verified,
                correctAnswer: this.resolveCorrectAnswerValue(previousQuestion),
                steps: previousQuestion.steps || '',
                ...previousDiagramState,
                diagramJsonText: null,
                diagramJsonError: '',
                isAdvancedPanelOpen: false,
                questions: updatedQuestions,
                difficultyLevel: updatedQuestions[prevIndex]?.difficulty || "",
                passageSetId: this.normalizeId(previousQuestion?.passageSetId || passageContext?.passageSetId),
                questionOrderWithinPassage: previousQuestion?.questionOrderWithinPassage || '',
                passageTitle: passageContext?.passageTitle || '',
                passageText: passageContext?.passageText || '',
                passageSkillsText: Array.isArray(passageContext?.passageSkills) ? passageContext.passageSkills.join('\n') : '',
                isFormDirty: false,
                saveError: '',
                isDiagramModalOpen: false,
                isEditMode: false,
                lastSavedQuestionSnapshot: previousQuestion ? this.cloneJsonValue(previousQuestion) : null,
            });
        } else {
            // For non-reviewers: use existing navigation logic (decrement index normally)
            if (currentQuestionIndex > 0) {
                const prevIndex = currentQuestionIndex - 1;
                const previousQuestion = questions[prevIndex];
                const commentData = this.buildQuestionCommentData(previousQuestion);
                const passageContext = this.getPassageContextFromQuestion(previousQuestion);
                const previousDiagramState = extractDiagramState(previousQuestion);

                this.setState({
                    question: previousQuestion.question,
                    options: previousQuestion.options || { A: "", B: "", C: "", D: "" },
                    currentQuestionIndex: prevIndex,
                    comment: commentData,
                    questionTitle: previousQuestion.question_title,
                    verified: previousQuestion.verified,
                    correctAnswer: this.resolveCorrectAnswerValue(previousQuestion),
                    steps: previousQuestion.steps || '',
                    ...previousDiagramState,
                    diagramJsonText: null,
                    diagramJsonError: '',
                    isAdvancedPanelOpen: false,
                    questions: updatedQuestions,
                    difficultyLevel: updatedQuestions[prevIndex]?.difficulty || "",
                    passageSetId: this.normalizeId(previousQuestion?.passageSetId || passageContext?.passageSetId),
                    questionOrderWithinPassage: previousQuestion?.questionOrderWithinPassage || '',
                    passageTitle: passageContext?.passageTitle || '',
                    passageText: passageContext?.passageText || '',
                    passageSkillsText: Array.isArray(passageContext?.passageSkills) ? passageContext.passageSkills.join('\n') : '',
                    isFormDirty: false,
                    saveError: '',
                    isDiagramModalOpen: false,
                    isEditMode: false,
                    lastSavedQuestionSnapshot: previousQuestion ? this.cloneJsonValue(previousQuestion) : null,
                });
            } else {
                alert('This is the first question');
            }
        }
    };





    // Toggle functions for modals
    toggleAddSubjectModal = () => {
        this.setState({
            isAddSubjectModalOpen: !this.state.isAddSubjectModalOpen
        });
    };

    toggleAddTopicModal = () => {
        this.setState({
            isAddTopicModalOpen: !this.state.isAddTopicModalOpen
        });
    };

    handleAddNewSubject = (values) => {
        const token = localStorage.getItem('studyapp.com');
        const newSubject = {
            courseId: this.state.courseId,  // Ensure courseId is included
            name: values.newSubjectName
        };

        fetch(baseUrl + 'subject', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(newSubject)
        })
            .then(response => response.json())
            .then(data => {
                // Update the subjects list with the new subject
                this.setState(prevState => ({
                    subjects: [...prevState.subjects, data], // Append new subject
                    isAddSubjectModalOpen: false // Close the modal
                }));
            })
            .catch(error => console.error('Error adding new subject:', error));
    };

    handleAddNewTopic = (values) => {
        const newTopic = {
            subjectId: this.state.subjectId,  // Ensure subjectId is included
            name: values.newTopicName,
            description: values.newTopicDescription  // Include the description
        };

        fetch(baseUrl + 'topic', {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(newTopic)
        })
            .then(response => response.json())
            .then(data => {
                // Update the topics list with the new topic for the selected subject
                this.setState(prevState => {
                    const updatedSubjects = prevState.subjects.map(subject => {
                        if (subject.subjectId === this.state.subjectId) {
                            return {
                                ...subject,
                                topics: [...subject.topics, {
                                    topicId: data._id,  // Map _id to topicId for consistency
                                    name: data.name,
                                    description: data.description // Store description in the state
                                }]  // Append new topic
                            };
                        }
                        return subject;
                    });

                    return {
                        subjects: updatedSubjects,  // Update the state with the new topics
                        isAddTopicModalOpen: false  // Close the modal
                    };
                }, () => {
                    // Callback runs after state is updated
                    this.handleSubjectChange(this.state.subjectId); // Now it's safe to call handleSubjectChange
                });
            })
            .catch(error => console.error('Error adding new topic:', error));
    };



    // This method will handle data from QuestionAnsComp
    handleQuestionDataChange(updatedData) {
        // alert(JSON.stringify(updatedData))
        // alert(JSON.stringify(this.state.questions))
        //this.state.questions
        this.setState({
            isFormDirty: true, // Mark form as dirty when any change is made
            saveError: '',
        });

        const { currentQuestionIndex, questions, difficultyLevel } = this.state;
        const nextDiagramPayload = buildQuestionDiagramPayload(updatedData);

        const updatedQuestions = [...this.state.questions];
        updatedQuestions[this.state.currentQuestionIndex] = {
            ...updatedQuestions[this.state.currentQuestionIndex],
            question_title: updatedData.questionTitle,
            question: updatedData.question,
            options: updatedData.options,
            correct_answer: updatedData.correctAnswer,
            correctAnswer: updatedData.correctAnswer,
            steps: updatedData.steps,
            ...nextDiagramPayload,
            difficulty: difficultyLevel, // Include difficulty level changes
        };

        this.setState({

            questionTitle: updatedData.questionTitle,
            question: updatedData.question,
            options: updatedData.options,
            correctAnswer: updatedData.correctAnswer,
            steps: updatedData.steps,
            ...nextDiagramPayload,
            questions: updatedQuestions,
            //skills: updatedData.skills
        });
    }

    // Method to handle course selection changes
    handleCourseChange(event) {

        let selectedCourseId
        if (typeof event === 'object') {
            selectedCourseId = event.target.value
        }
        else {
            selectedCourseId = event
        }

        // alert("selectedCourseId " + selectedCourseId)
        // alert("this.state.courseId " + this.state.courseId)

        //  event.target.value ? event.target.value : this.state.selectedCourse
        //selectedCourseId = event && event.target.value ? event.target.value: this.state.selectedCourse ;
        const selectedCourse = this.state.courses.find(course => course._id === selectedCourseId);
        const selectedCourseName = selectedCourse ? selectedCourse.name : "";

        this.setState({
            courseId: selectedCourseId,
            selectedCourseName: selectedCourseName,
            courseSelectionRequired: !selectedCourseId
        }, () => {
            if (selectedCourseId) {
                setActiveCourseId(selectedCourseId);
                this.fetchSubjectsAndTopics(selectedCourseId);
            }
        });
    }


    fetchQuestionFromOpenAI = () => {
        const subj = this.state.selectedSubject
        const topic = this.state.selectedTopic
        const difficulty = this.state.difficultyLevel

        // If generating variations, pass the current question, correct answer, and steps to OpenAI
        const isGeneratingVariation = this.state.generateVariations;
        const currentQuestion = this.state.question;
        const currentCorrectAnswer = this.state.correctAnswer;
        const currentSteps = this.state.steps;
        const isDiagram = this.state.diagrams;


        this.props.getOpenAIText(this.state.gptModel, this.state.modelUrl, this.state.prompt,

            () => {
                //alert("call back called " + this.props.openAIResponse.openAIReponse)
                const data = this.props.openAIResponse.openAIReponse
                const content = data.choices[0].message.content;
                const parsedData = JSON.parse(content);
                const nextDiagramPayload = buildQuestionDiagramPayload(parsedData);
                const normalizedCorrectAnswer = this.resolveCorrectAnswerValue({
                    correct_answer: parsedData.correct_answer,
                    options: parsedData.options,
                });

                const variationId = this.state.variationId ? this.state.variationId : uuid();  // Generate variationId if not present

                //console.log("parsedData " + JSON.stringify(parsedData))

                this.setState((prevState) => ({
                    comment: data,
                    questionTitle: isGeneratingVariation ? prevState.questionTitle : parsedData.question_title,  // Retain the title if generating variations
                    variationId: isGeneratingVariation ? prevState.variationId : uuid(),  // Retain the variationId if generating variations
                    question: parsedData.question,
                    options: parsedData.options,
                    correctAnswer: normalizedCorrectAnswer,
                    steps: parsedData.steps.join('\n'),
                    ...nextDiagramPayload,
                    mode: "add",
                }), () => {
                    //   alert("this.state.mode " + this.state.mode)
                });

            }
        );



    }

    handleSubjectChange = (selectedSubject, callback) => {
        const normalizedSubjectId = this.normalizeId(selectedSubject);
        const selectedSub = this.state.subjects.find(
            sub => this.normalizeId(sub.subjectId) === normalizedSubjectId
        );

        if (!selectedSub) {
            console.error("Selected subject not found in subjects");
            return;
        }

        this.setState({
            subjectId: selectedSub.subjectId,
            selectedSubject: selectedSub.name,
            topics: selectedSub.topics || [],
            // Reset dependent IDs on subject change to avoid stale topic/subtopic values.
            selectedTopic: '',
            topicId: '',
            selectedSubtopic: '',
            subtopicId: '',
            selectedSubtopicName: '',
            skills: []
        }, () => {
            if (isFunction(callback)) {
                callback();
            }

            if (this.state.selectedSubject) {
                this.fetchPrompt();  // Fetch prompt if a topic is already selected
            }
        });
    };



    handleTopicChange = (selectedTopic, callback) => {
        const normalizedTopicId = this.normalizeId(selectedTopic);
        const selectedTop = this.state.topics.find(
            top => this.normalizeId(top.topicId) === normalizedTopicId
        );

        if (!selectedTop) {
            console.error("Selected topic  not found");
            return;
        }

        this.setState({
            topicId: selectedTop.topicId,
            selectedTopic: selectedTop.name,
            skills: selectedTop.skills || [],
            // Topic change invalidates any previously selected subtopic.
            selectedSubtopic: "",
            subtopicId: "",
            selectedSubtopicName: ""
        }, () => {
            if (isFunction(callback)) {
                callback();
            }
            if (this.state.subjectId) {
                this.fetchPrompt();  // Fetch prompt if a subject is already selected
            }
        });
    };

    handleSubTopicChange = (selectedSubTopic) => {
        const normalizedTopicId = this.normalizeId(this.state.topicId);
        const normalizedSubtopicId = this.normalizeId(selectedSubTopic);
        const selectedTop = this.state.topics.find(
            top => this.normalizeId(top.topicId) === normalizedTopicId
        );
        const selectedSubTop = selectedTop?.subtopics?.find(
            subtop => this.normalizeId(subtop.subtopicId) === normalizedSubtopicId
        );

        if (!selectedSubTop) {
            console.error("Selected subtopic  not found");
            return;
        }

        this.setState({
            selectedSubtopic: selectedSubTop.subtopicId,
            subtopicId: selectedSubTop.subtopicId,
            selectedSubtopicName: selectedSubTop.name || '',
            //skills: selectedSubTop.skills
        }, () => {
            if (this.state.subjectId) {
                this.fetchPrompt();  // Fetch prompt if a subject is already selected
            }
        });
    };


    handleDifficultyChange = (difficuly) => {
        //alert ("difficultyLevel " + difficuly);

        if (difficuly == NaN) {
            alert("difficulty level should be a number from 1 - 5");
            return;
        } else if (difficuly > 5 || difficuly < 1) {
            alert("difficulty level should be a number from 1 - 5");
        }

        this.setState({
            difficultyLevel: difficuly
            //skills: selectedSubTop.skills
        }, () => {
            if (this.state.subjectId) {
                this.fetchPrompt();  // Fetch prompt if a subject is already selected
            }
        });
    };


    handleSkillsChange = (selectedSkills) => {
        //alert ("selectedSkills " + selectedSkills);
        //this.setState({ selectedSkills });
    };



    handleDateChange(date) {
        this.setState({
            startDate: date
        });
        // const d = new Date(date);
        // const dIso = d.toISOString();
        // alert (d.toISOString());
        // alert (new Intl.DateTimeFormat('en-AU', { year: 'numeric', month: 'short', day: '2-digit' }).format(new Date(Date.parse(dIso))));

    }

    handleTaskClick(taskId) {
        //alert("handleTaskClick " + taskId);

        this.setState({
            taskId: taskId
        });

    }

    handleAddPredefinedTask(task) {
        const newTask = {
            ...task,
            due: new Date(), // Set a default due date or allow user to pick
            status: 'open' // Default status
        };

        // Assuming postTaskCommon is passed via props or available in scope
        this.props.postTaskCommon(newTask, () => {
            //console.log("Task added successfully");
            // Optionally refresh the task list or give user feedback
        });
    };



    //this is to reflect the changes made to the text boxes right away in the form by 
    //updating the state through redux reducer function
    handleChange(event, taskId) {
        // alert("event.target.id " + event.target.id)
        //alert("event.target.value " + event.target.value)
        //alert ("event.target.id " + event.target.id)
        //this.state.isModalOpen

        if (event.target.id == "taskId") {
            //const { id, value } = event.target;
            this.setState({
                taskId: event.currentTarget.getAttribute('data-task-id')
            });
        }
        else if (event.target.id == "taskStatus") {
            const linkedTasks = this.props.allTasks.alltasks;
            //alert("event.target.value " + event.target.value);
            //alert("filteredTask " + JSON.stringify(linkedTasks));


            const filteredTask = linkedTasks.filter(task => task._id === taskId);
            //alert("filteredTask " + JSON.stringify(filteredTask[0].task));

            const task = {

                task: filteredTask[0].task,
                desc: filteredTask[0].desc,
                priority: filteredTask[0].priority,
                due: filteredTask[0].due,
                status: event.target.checked === true ? "done" : "open",
                subject: filteredTask[0].subject,
                topic: filteredTask[0].topic,
                subtopic: filteredTask[0].subtopic,
                dailylogid: filteredTask[0].dailylogid,
                projectid: "",
                parentid: ""
            }

            this.props.updateTaskCommon(task, taskId, this.updateTaskId);
        }
        else {
            const { id, value } = event.target;
            this.setState({
                [id]: value,
            });

        }




    }

    componentWillUnmount() {
        // Clear pending inline copy feedback so it cannot update state after this screen unmounts.
        if (this.copyStatusTimer) {
            clearTimeout(this.copyStatusTimer);
        }
    }


    componentDidMount() {
        // Course resolution order: props -> active course in localStorage -> explicit admin selection.
        const courseId = this.props.courseId || getActiveCourseId();
        // Always load full course options so the dropdown label reflects the currently selected course.
        this.fetchCoursesList();
        if (courseId) {
            this.setState(
                {
                    courseId: this.normalizeId(courseId),
                    courseSelectionRequired: false
                },
                () => {
                    this.handleCourseChange(this.state.courseId);
                }
            );
        } else {
            this.setState({ courseSelectionRequired: true });
        }

        this.fetchPrompt();



    }

    fetchCoursesList = () => {
        fetch(baseUrl + 'courses', { headers: authHeaders() })
            .then(response => response.json())
            .then(data => {
                const courses = Array.isArray(data) ? data : [];
                this.setState({ courses });
            })
            .catch(error => console.error('Error fetching courses:', error));
    };

    fetchSubjectsAndTopics = (courseId) => {
        if (!courseId) {
            return;
        }
        const token = localStorage.getItem('studyapp.com');
        // Multi-course: admin subject tree calls are authenticated so hidden-course access can be server-validated.
        // Fetch subjects and topics for the selected course
        fetch(baseUrl + `courses/${courseId}/subjects-topics`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        })
            .then(response => response.json())
            .then(data => {
                // Data contains subjects and topics
                console.log("data 123 " + JSON.stringify(data))
                // alert("data 123 " + JSON.stringify(data))
                const mappedSubjects = data.map(subject => ({
                    subjectId: subject._id,
                    name: subject.name,
                    topics: (subject.topics || []).map(topic => ({
                        topicId: topic._id,
                        name: topic.name,
                        subtopics: (topic.subtopics || []).map(subtopic => ({
                            subtopicId: subtopic._id,
                            name: subtopic.name
                        }))
                    }))
                }));

                this.setState({ subjects: mappedSubjects }, () => {
                    // Re-sync edit selection after async hierarchy load so topic/subtopic show in modal reliably.
                    const currentQuestion = Array.isArray(this.props.question)
                        ? this.props.question[this.props.currentQuestionIndex]
                        : null;
                    const subjectIdToSync = currentQuestion?.subjectId || this.state.subjectId || this.props.preselectedSubject;
                    const topicIdToSync = currentQuestion?.topicId || this.state.topicId || this.props.preselectedTopic;
                    const subtopicIdToSync = currentQuestion?.subtopicId || this.state.subtopicId || this.props.preselectedSubtopic;
                    this.syncSelectionFromIds(subjectIdToSync, topicIdToSync, subtopicIdToSync);
                });
            })
            .catch(error => console.error('Error fetching subjects and topics:', error));
    };

    // In your AddEditQuestions component

    fetchPrompt = async () => {
        const { courseId, subjectId, topicId, questionType } = this.state;
        const subj = this.state.selectedSubject;
        const topic = this.state.selectedTopic;
        const subtopic = this.state.selectedSubtopicName;
        let difficulty = this.state.difficultyLevel;
        const { generateVariations, question, correctAnswer, steps, diagrams, difficultyAdjustment, numQuestions } = this.state;

        if (!courseId || !subjectId || !topicId || !questionType) return;

        try {
            const params = new URLSearchParams({
                courseId,
                subjectId,
                questionType,
            });
            if (topicId) {
                params.append('topicId', topicId);
            }
            if (this.state.selectedSubtopic) {
                params.append('subtopicId', this.state.selectedSubtopic);
            }

            const response = await fetch(`${baseUrl}prompts/final?${params.toString()}`, { headers: authHeaders() });


            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            //alert("data " + JSON.stringify(data));
            // Ensure all required sections are present
            const requiredSections = [
                'basePrompt',
                'difficultySection',
                'guidelines',
                'sampleQuestion',
                'difficultyDefinitions',
                'additionalInstructions'
            ];
            const hasAllSections = requiredSections.every(section => data[section]);
            //  alert("hasAllSections " + hasAllSections);

            if (data && hasAllSections) {
                // Start with the subject-level prompt
                let fetchedPrompt = data.basePrompt;

                // Append topic-level guidelines if available
                if (data.topicGuidelines && data.topicGuidelines.trim().length > 0) {
                    fetchedPrompt += `\n${data.topicGuidelines}`;
                }

                // Append subtopic-level guidelines if available
                if (data.subtopicGuidelines && data.subtopicGuidelines.trim().length > 0) {
                    fetchedPrompt += `\n${data.subtopicGuidelines}`;
                }

                // Append additional sections from the subject prompt
                const variationPrompt = generateVariations ? `
    You are asked to generate a variation of the following multiple-choice question. Change the numbers in the question, options, and correct answer, while keeping the same structure. Ensure that the steps remain logically correct and the answer changes accordingly.
    
    Original Question: "${this.state.sampleQuestion}"
    Original Answer Options: "${this.state.sampleAnswerOptions}"
    Original Correct Answer: "${this.state.sampleCorrectAnswer}"    
    Original Steps: "${this.state.samplesteps}"
    
    Generate new versions of this question with different numbers and answers but with the same format and response structure.
                ` : '';

                const diagramPrompt = diagrams ? 'question must have a diagram.' : '';

                // Add difficulty adjustment if required
                if (difficultyAdjustment === 'harder') {
                    fetchedPrompt += ` Make the new question harder than the current question: "${this.state.sampleQuestion}". Ensure the steps remain logically valid but more challenging.`;
                    difficulty = difficulty < 5 ? difficulty + 1 : difficulty;
                } else if (difficultyAdjustment === 'simpler') {
                    fetchedPrompt += ` Make the new question simpler than the current question: "${this.state.sampleQuestion}". Ensure the steps remain logically valid but easier.`;
                    difficulty = difficulty > 1 ? difficulty - 1 : difficulty;
                }

                // Incorporate the remaining prompt sections
                fetchedPrompt += `
    ${data.difficultySection}
    
    ${data.guidelines}
    
    ${data.sampleQuestion}
    
    ${data.difficultyDefinitions}
    
    ${data.additionalInstructions}
                `;

                const hasPassageContext = Boolean(this.state.passageText && this.state.passageText.trim());
                if (hasPassageContext) {
                    // Passage-based questions need richer reasoning so reviewers and students can trace the answer back to evidence.
                    fetchedPrompt += `

For this passage-based question:
- The correct_answer field must contain the full answer text, not just the option label.
- The steps must be detailed and evidence-based, not a short 1-2 line summary.
- Refer explicitly to the relevant parts of the passage that support the answer.
- Explain why the correct option is supported by the passage and why the strongest distractors are not fully supported.
- Where possible, provide at least 3 to 5 clear reasoning steps that move from passage evidence to final answer.
                    `;
                }

                // Replace placeholders with actual values
                let populatedPrompt;

                if (!generateVariations) {
                    populatedPrompt = fetchedPrompt
                        .replace('${subj}', subj)
                        .replace('${topic}', topic)
                        .replace('${subtopic}', subtopic)
                        .replace('${difficulty}', difficulty)
                        .replace('${variationPrompt}', variationPrompt)
                        .replace('${diagramPrompt}', diagramPrompt)
                        .replace('${numQuestions}', numQuestions === 1 ? "a" : numQuestions)
                        .replace('${sampleQuestion}', this.state.sampleQuestion || "")
                        .replace('${sampleAnswerOptions}', this.state.sampleAnswerOptions || "")
                        .replace('${sampleCorrectAnswer}', this.state.sampleCorrectAnswer || "")
                        .replace('${sampleAnswerSteps}', this.state.samplesteps || "");
                } else {
                    populatedPrompt = fetchedPrompt
                        .replace('${subj}', subj)
                        .replace('${topic}', topic)
                        .replace('${subtopic}', subtopic)
                        .replace('${difficulty}', difficulty)
                        .replace('${variationPrompt}', variationPrompt)
                        .replace('${diagramPrompt}', diagramPrompt)
                        .replace('${numQuestions}', numQuestions === 1 ? "a" : numQuestions);
                }
                //alert("populatedPrompt " + populatedPrompt);

                this.setState({
                    prompt: populatedPrompt,
                    //gptModel: data.model,
                    //modelUrl: data.model.url
                });
            } else {
                console.warn("Prompt data is missing required sections.");
                this.setState({ prompt: '' });
            }
        } catch (error) {
            console.error('Error fetching prompt:', error);
            this.setState({ prompt: '' });
        }
    };

    toggleNav() {
        this.setState({
            isNavOpen: !this.state.isNavOpen
        });
    }


    // In your parent component
    updateTaskId = (newTaskId) => {
        //        alert ("update task id called ")
        this.setState({
            // dailylogid: "",
            //subject: "",
            // topic: "",
            // subtopic: "",
            //calledfrom: "",
            taskId: "",
            //allTasks: this.props.allTasks
        }, () => {
            // Code to execute after state is updated
            // This will be called once the state is updated and the component is re-rendered
            // You can place your logic or function calls here

        });
    }


    toggleModal() {


    }

    handleLogin(event) {

        this.toggleModal();
        // alert("Username: " + this.username.value + " Password:" + this.password.value);
        // alert("Username: " + this.username.value + " Password:" + this.password.value
        //     + " Remember: " + this.remember.checked);
        event.preventDefault();
    }

    handleBulkSaveFromJson = () => {
        try {

            if (!this.state.difficultyLevel) {
                alert('Difficulty level must be between 1 and 5.');
                return;
            } else if (this.state.difficultyLevel < 1 || this.state.difficultyLevel > 5) {
                alert('Difficulty level must be between 1 and 5.');
                return;
            }

            const parsedData = JSON.parse(this.state.bulkQuestionsJsonInput);  // Parse JSON
            const variationId = this.state.variationId ? this.state.variationId : uuid();

            // Check if parsedData has the right format
            if (!parsedData.questions || !Array.isArray(parsedData.questions)) {
                throw new Error("JSON should contain an array under 'questions'.");
            }

            // Save each question in the array
            parsedData.questions.forEach((question) => {
                const questionDiagramPayload = buildQuestionDiagramPayload(question);
                // Bulk JSON often carries steps as an array of lines, while the
                // current question schema persists a single newline-delimited string.
                const normalizedSteps = Array.isArray(question.steps)
                    ? question.steps.join('\n')
                    : (question.steps || '');
                const questionData = {
                    question_title: question.question_title,
                    question: question.question,
                    options: question.options,
                    correct_answer: question.correct_answer,
                    steps: normalizedSteps,
                    ...questionDiagramPayload,
                    subject: this.state.subject,
                    topic: this.state.topic,
                    difficulty: this.state.difficultyLevel,
                    questionType: this.state.questionType,
                    subjectId: this.state.subjectId,
                    topicId: this.state.topicId,
                    // Reading-comprehension questions may not belong to a subtopic, so omit empty values.
                    subtopicId: this.state.selectedSubtopic || undefined,
                    courseId: this.state.courseId,
                    variationId: variationId,// Generate a unique variationId if not present
                    // Purpose metadata keeps bulk-created drills out of the normal exam-style question pool.
                    questionPurpose: normalizeQuestionPurpose(question.questionPurpose || question.question_purpose || this.state.questionPurpose),
                    remediationStage: question.remediationStage || question.remediation_stage || this.state.remediationStage || '',
                    targetSubskills: normalizeTargetSubskills(question.targetSubskills || question.target_subskills).length
                        ? normalizeTargetSubskills(question.targetSubskills || question.target_subskills)
                        : this.parseTargetSubskillsFromState(),
                };

                const token = localStorage.getItem('studyapp.com');
                fetch(`${baseUrl}questions`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                    },
                    body: JSON.stringify(questionData),
                })
                    .then(response => response.json())
                    .then(data => console.log('Question saved successfully:', data))
                    .catch(error => console.error('Error saving question:', error));
            });

            // Clear the text area and error on successful save
            this.setState({ bulkQuestionsJsonInput: '', parseError: '' });
            alert('Questions saved successfully!');

        } catch (error) {
            // Display error if JSON parsing fails
            this.setState({ parseError: `Invalid JSON: ${error.message}` });
        }
    };


    handleBulkSave = () => {
        const questionsArray = JSON.parse(this.state.comment);

        const { subject, topic, subtopic, questionType, difficultyLevel, selectedCourse, selectedSkills, variationId } = this.state;


        questionsArray.questions.forEach(question => {
            const questionDiagramPayload = buildQuestionDiagramPayload(question);
            // Generated bulk payloads can represent steps as an array, but the
            // persistence layer still expects one newline-delimited string.
            const normalizedSteps = Array.isArray(question.steps)
                ? question.steps.join('\n')
                : (question.steps || '');
            const questionData = {
                question_title: question.question_title,
                question: question.question,
                options: question.options,
                correct_answer: question.correct_answer,
                steps: normalizedSteps,
                skills: this.getSelectedSkillIds(),  // Skills can be stored as ids or hydrated objects after previous saves.
                subject,
                topic,
                subjectId: this.state.subjectId,
                topicId: this.state.topicId,
                // Keep optional hierarchy values out of the payload when they are blank.
                subtopicId: this.state.selectedSubtopic || undefined,
                questionType,  // Include question type in the payload
                difficulty: difficultyLevel,  // Include difficulty level in the payload
                courseId: this.state.courseId,
                variationId: this.state.variationId,// Generate a unique variationId if not present
                // Purpose metadata keeps bulk-created drills out of the normal exam-style question pool.
                questionPurpose: normalizeQuestionPurpose(question.questionPurpose || question.question_purpose || this.state.questionPurpose),
                remediationStage: question.remediationStage || question.remediation_stage || this.state.remediationStage || '',
                targetSubskills: normalizeTargetSubskills(question.targetSubskills || question.target_subskills).length
                    ? normalizeTargetSubskills(question.targetSubskills || question.target_subskills)
                    : this.parseTargetSubskillsFromState(),
                ...questionDiagramPayload
            };

            const token = localStorage.getItem('studyapp.com');
            fetch(`${baseUrl}questions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify(questionData),
            })
                .then(response => response.json())
                .then(data => console.log('Question saved successfully:', data))
                .catch(error => console.error('Error saving question:', error));
        });
    };


    async handleUpdateLog(values, options = {}) {
        const { subject, topic, subtopic, questionType, difficultyLevel, selectedCourse, selectedSkills, variationId } = this.state;


        // Prepare the question data from the QuestionAnsComp

        if (!difficultyLevel) {
            this.setState({
                saveError: 'Difficulty level must be between 1 and 5.'
            });
            return;
        } else if (difficultyLevel < 1 || difficultyLevel > 5) {
            this.setState({
                saveError: 'Difficulty level must be between 1 and 5.'
            });
            return;
        }

        const questionData = {
            question_title: this.state.questionTitle,  // Save the combined title and variationId
            question: this.state.question,  // From QuestionAnsComp
            options: this.state.options,  // From QuestionAnsComp
            correct_answer: this.state.correctAnswer,  // From QuestionAnsComp
            steps: this.state.steps,  // From QuestionAnsComp
            skills: this.getSelectedSkillIds(),  // Skills can be stored as ids or hydrated objects after previous saves.
            subject,
            topic,
            subjectId: this.state.subjectId,
            topicId: this.state.topicId,
            // Reading-comprehension edits can clear subtopic linkage, so avoid sending an empty string.
            subtopicId: this.state.subtopicId || undefined,
            questionType,  // Include question type in the payload
            difficulty: difficultyLevel,  // Include difficulty level in the payload
            courseId: this.state.courseId,
            variationId: this.state.variationId,// Generate a unique variationId if not present
            ...buildQuestionDiagramPayload({
                diagramFormat: this.state.diagramFormat,
                diagramCode: this.state.diagramCode,
                diagramScene: this.state.diagramScene,
            }),
            // Passage fields are only sent when the question is part of a shared reading stimulus.
            passageSetId: this.state.passageSetId || undefined,
            questionOrderWithinPassage: this.state.questionOrderWithinPassage || undefined,
            sharedStimulusSnapshot: this.buildPassageSnapshotFromState(),
            // Question purpose controls where the question appears: exam pool, remediation drill pool, or challenge inventory.
            questionPurpose: normalizeQuestionPurpose(this.state.questionPurpose),
            remediationStage: this.state.remediationStage || '',
            targetSubskills: this.parseTargetSubskillsFromState(),
        };

        this.setState({
            isSaving: true,
            saveError: '',
            saveSuccess: false,
        });


        // alert("handleupdatelog " + JSON.stringify(this.props.question))
        // alert("questionData scrambled " + JSON.stringify(questionData))

        //alert("this.state.mode 2ns" + this.state.mode)

        const isUpdateMode = this.state.mode === "update";
        const method = isUpdateMode ? "PUT" : "POST";  // Determine if updating or adding
        const url = this.state.mode === "update"
            ? `${baseUrl}questions/${this.state.questions[this.state.currentQuestionIndex]._id}`  // Use the question's ID for updating
            : `${baseUrl}questions`;  // Use POST for creating new questions

        try {
            const token = localStorage.getItem('studyapp.com');
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify(questionData)
            });
            const savedQuestion = await response.json().catch(() => null);
            if (!response.ok) {
                throw new Error(savedQuestion?.error || savedQuestion?.message || response.statusText || 'Save failed');
            }

            // Persist saved data back into local modal state and notify parent list immediately.
            this.setState((prevState) => {
                const updatedQuestions = Array.isArray(prevState.questions) ? [...prevState.questions] : [];
                let nextQuestionIndex = prevState.currentQuestionIndex;
                const savedPassageContext = this.getPassageContextFromQuestion(savedQuestion || {});

                if (isUpdateMode) {
                    if (updatedQuestions[nextQuestionIndex]) {
                        updatedQuestions[nextQuestionIndex] = {
                            ...updatedQuestions[nextQuestionIndex],
                            ...(savedQuestion || {})
                        };
                    }
                } else if (savedQuestion) {
                    updatedQuestions.unshift(savedQuestion);
                    nextQuestionIndex = 0;
                }

                return {
                    questions: updatedQuestions,
                    currentQuestionIndex: nextQuestionIndex,
                    question: savedQuestion?.question || prevState.question,
                    questionTitle: savedQuestion?.question_title || prevState.questionTitle,
                    options: savedQuestion?.options || prevState.options,
                    correctAnswer: savedQuestion?.correct_answer || prevState.correctAnswer,
                    steps: savedQuestion?.steps || prevState.steps,
                    selectedSkills: Array.isArray(savedQuestion?.skills) ? savedQuestion.skills : prevState.selectedSkills,
                    ...buildQuestionDiagramPayload(savedQuestion || {
                        diagramFormat: prevState.diagramFormat,
                        diagramCode: prevState.diagramCode,
                        diagramScene: prevState.diagramScene,
                    }),
                    passageSetId: this.normalizeId(savedQuestion?.passageSetId || savedPassageContext?.passageSetId || prevState.passageSetId),
                    questionOrderWithinPassage: savedQuestion?.questionOrderWithinPassage || prevState.questionOrderWithinPassage,
                    passageTitle: savedPassageContext?.passageTitle || prevState.passageTitle,
                    passageText: savedPassageContext?.passageText || prevState.passageText,
                    passageSkillsText: Array.isArray(savedPassageContext?.passageSkills) ? savedPassageContext.passageSkills.join('\n') : prevState.passageSkillsText,
                    questionPurpose: normalizeQuestionPurpose(savedQuestion?.questionPurpose || prevState.questionPurpose),
                    remediationStage: savedQuestion?.remediationStage || prevState.remediationStage,
                    targetSubskillsText: Array.isArray(savedQuestion?.targetSubskills) ? savedQuestion.targetSubskills.join('\n') : prevState.targetSubskillsText,
                    saveSuccess: true,
                    isSaving: false,
                    isFormDirty: false,
                    isEditMode: options.exitEditMode ? false : prevState.isEditMode,
                    saveError: '',
                    lastSavedQuestionSnapshot: savedQuestion ? this.cloneJsonValue(savedQuestion) : prevState.lastSavedQuestionSnapshot,
                };
            }, () => {
                if (this.props.onUpdateQuestions) {
                    this.props.onUpdateQuestions(this.state.questions, this.state.currentQuestionIndex);
                }
            });

            // Hide the success message after 3 seconds
            setTimeout(() => {
                this.setState({ saveSuccess: false });
            }, 3000);

            return true;
        } catch (error) {
            const userFriendlyMessage = this.buildQuestionSaveErrorMessage(error);
            console.error('Error saving question:', error);
            this.setState({
                isSaving: false,
                saveError: userFriendlyMessage,
                saveSuccess: false,
            });
            return false;
        }
    }


    handleAccept() {
        if (!this.ensureNoPendingEdits('accept the question')) {
            return;
        }

        const currentQuestion = this.state.questions[this.state.currentQuestionIndex];
        if (!currentQuestion || !currentQuestion._id) {
            alert("No question selected.");
            return;
        }
        const token = localStorage.getItem('studyapp.com');
        fetch(`${baseUrl}questions/${currentQuestion._id}/accept`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ workflow: "accepted" })
        })
            .then(response => response.json())
            .then(updatedQuestion => {
                // Update the question in the state with the accepted workflow\n
                const updatedQuestions = [...this.state.questions];
                updatedQuestions[this.state.currentQuestionIndex] = updatedQuestion;
                this.setState({
                    questions: updatedQuestions,
                    verifiedBy: updatedQuestion.verifiedBy,
                    incorrectBy: updatedQuestion.incorrectBy,
                    saveSuccess: true
                }, () => {
                    // Optionally perform additional UI updates
                });
                setTimeout(() => {
                    this.setState({ saveSuccess: false });
                }, 3000);
            })
            .catch(error => {
                console.error("Error accepting question:", error);
                alert("Failed to accept question.");
            });
    }

    async handleDeleteCurrentQuestion() {
        if (!this.ensureNoPendingEdits('delete the question')) {
            return;
        }

        const currentQuestion = this.state.questions[this.state.currentQuestionIndex];
        if (!currentQuestion || !currentQuestion._id) {
            alert("No question selected.");
            return;
        }
        if (!this.props.onDeleteQuestion) {
            alert("Delete is not available from this screen.");
            return;
        }

        // Parent owns the API call so the manager list and verify modal stay synchronized after deletion.
        await this.props.onDeleteQuestion(currentQuestion);
    }



    handleAddTask() {
        const unique_id = uuid();
        var timestamp = new Date().toISOString();

        // console.log(timestamp + ' errormsg ' + this.props.tasks.errMess);

        this.setState({
            isCommonTaskOpen: true
        })


    }

    handleSubmit(values) {


        const unique_id = uuid();
        var timestamp = new Date().toISOString();

        const { mode, subject, date } = this.props;

        if (mode === "add") {
            // Call a new action creator to save the new daily log

            this.props.postComment(
                unique_id,
                new Date(date),
                subject,
                values.topic,
                values.subtopic,
                values.comment
            );
        } else {
            // Existing logic for updating an existing daily log
            // ...
            if (!this.state.chkDontClose) {
                this.toggleModal();
            }

            this.setState({
                savedMessage: "Details Saved",
            });



            // console.log(timestamp + ' errormsg ' + this.props.tasks.errMess);

            if (this.props.tasks.errMess != null) {
                var timestamp = new Date().toISOString();
                // console.log(timestamp + ' inside addnewtask');
                this.props.addNewTask(unique_id, this.props.dailylog.id, values.topic);

            }
            else {
                var timestamp = new Date().toISOString();
                //  console.log(timestamp + ' inside postask ' + JSON.stringify(this.props.tasks.tasks));
                this.props.postTask(unique_id, this.props.dailylog.id, this.props.tasks.tasks, values.topic);

            }
        }



        //console.log("current state is :" + JSON.stringify(values));
        //alert("current state is :" + JSON.stringify(values));
        // alert(this.props.dishId);
        //alert(values.rating);
        //alert(values.author);
        //alert(values.comment);

    }
    renderTasks() {
        const tasks = this.props.allTasks.alltasks; // Assuming tasks are passed as props
        const currentDate = new Date();

        // Filter for outstanding tasks and sort by due date
        const filteredTasks = tasks
            .filter(task => task.status !== 'done' && task.subject === this.state.subject)
            .sort((a, b) => new Date(a.due) - new Date(b.due));

        return filteredTasks.map((task, index) => {
            const dueDate = new Date(task.due);
            const isPastDue = dueDate < currentDate;
            return (
                <Row key={index} >
                    <Col md={2}>
                        <input
                            id="taskStatus"
                            type="checkbox"
                            onChange={(event) => this.handleChange(event, task._id)}
                            checked={task.status === 'done'}
                            value={task._id}
                        />
                    </Col>
                    <Col md={10}>
                        <span id="taskId"
                            className="task-title"
                            onClick={(event) => this.handleChange(event, task._id)}
                            data-task-id={task._id}
                        >
                            {task.task}
                        </span>
                        <div className="task-desc">{task.desc}</div>
                        <div style={{ color: isPastDue ? 'red' : 'black' }}>
                            Due: {moment(task.due).format('DD MMM YYYY')}
                        </div>
                    </Col>

                </Row>
            );
        });
    }

    componentDidUpdate(prevProps, prevState) {



        const currentQuestionIndex = this.props.currentQuestionIndex;
        const questions = this.props.question;



        // alert("questions " + JSON.stringify(questions))

        //  alert("index  " + JSON.stringify(currentQuestionIndex))

        // Access the current question for display
        const currentQuestion = Array.isArray(questions) ? questions[currentQuestionIndex] : null;
        //alert("currentQuestion " + JSON.stringify(currentQuestion))



        //if (this.props.mode === 'edit' && this.props.isOpen) {
        if (prevProps.isOpen != this.props.isOpen && this.props.isOpen && this.props.calledFrom == "qmanager") {
            // Course in modal should follow parent QuestionManager selection first to avoid stale course display.
            const selectedCourseId = this.normalizeId(this.props.courseId || currentQuestion?.courseId || this.state.courseId);
            const selectedSubjectId = this.normalizeId(currentQuestion?.subjectId);
            const selectedTopicId = this.normalizeId(currentQuestion?.topicId);
            const selectedSubtopicId = this.normalizeId(currentQuestion?.subtopicId);
            const passageContext = this.getPassageContextFromQuestion(currentQuestion);
            const normalizedCorrectAnswer = this.resolveCorrectAnswerValue(currentQuestion);
            const currentDiagramState = extractDiagramState(currentQuestion);

            //alert("AddEditQ inside this.props.question " + JSON.stringify(this.props.question))
            this.setState({
                currentQuestionIndex: currentQuestionIndex,
                questions: questions,
                question: currentQuestion ? currentQuestion.question : "",
                options: this.props.question ? this.props.question[currentQuestionIndex].options : { A: "", B: "", C: "", D: "" },
                correctAnswer: normalizedCorrectAnswer,
                steps: this.props.question ? this.props.question[currentQuestionIndex].steps : "",
                questionTitle: this.props.question ? this.props.question[currentQuestionIndex].question_title : "",
                variationId: this.props.question ? this.props.question[currentQuestionIndex].variationId : uuid(),  // Retain variationId for existing questions
                // In edit mode, course must come from the question to keep hierarchy context correct.
                courseId: selectedCourseId,
                subjectId: selectedSubjectId,
                topicId: selectedTopicId,
                subtopicId: selectedSubtopicId,
                selectedSubtopic: selectedSubtopicId,
                comment: this.props.comment ? this.props.comment : "",
                ...currentDiagramState,
                passageSetId: this.normalizeId(currentQuestion?.passageSetId || passageContext?.passageSetId),
                questionOrderWithinPassage: currentQuestion?.questionOrderWithinPassage || '',
                passageTitle: passageContext?.passageTitle || '',
                passageText: passageContext?.passageText || '',
                passageSkillsText: Array.isArray(passageContext?.passageSkills) ? passageContext.passageSkills.join('\n') : '',
                mode: "update",
                buttonText: "Update",
                verified: currentQuestion?.verified || false,
                verifiedBy: currentQuestion?.verifiedBy ? currentQuestion.verifiedBy.email : null, // Adjust based on your backend response
                isVerifierMode: this.props.isVerifierMode || false, // Default to false
                difficultyLevel: this.props.question ? this.props.question[currentQuestionIndex].difficulty : "",
                selectedSkills: Array.isArray(currentQuestion?.skills) ? currentQuestion.skills : [],
                questionPurpose: normalizeQuestionPurpose(currentQuestion?.questionPurpose),
                remediationStage: currentQuestion?.remediationStage || '',
                targetSubskillsText: Array.isArray(currentQuestion?.targetSubskills) ? currentQuestion.targetSubskills.join('\n') : '',
                isFormDirty: false,
                saveError: '',
                saveSuccess: false,
                isSaving: false,
                isEditMode: false,
                isDiagramModalOpen: false,
                lastSavedQuestionSnapshot: currentQuestion ? this.cloneJsonValue(currentQuestion) : null,
                // Update other fields as necessary
            }, () => {
                // Load subject tree for the question's course first, then sync subject/topic/subtopic IDs.
                if (selectedCourseId && this.normalizeId(prevState.courseId) !== selectedCourseId) {
                    this.handleCourseChange(selectedCourseId);
                } else {
                    this.syncSelectionFromIds(selectedSubjectId, selectedTopicId, selectedSubtopicId);
                }
            });

            if (this.props.isReviewer) {
                // For reviewers: only navigate unreviewed questions
                // console.log ("questions 123 " + JSON.stringify(questions))
                const questionsArray = Array.isArray(questions)
                    ? this.props.question
                    : [questions];

                const unreviewedQuestions = questionsArray.filter(q => !q.verifiedBy && !q.incorrectBy);
                //alert("unreviewedQuestions " + unreviewedQuestions.length)
                this.setState({
                    unreviewedQuestionsState: unreviewedQuestions,
                })

            }

            //  alert("this.props.question.subjectId " + this.props.question ? this.props.question[currentQuestionIndex].subtopicId : ""  )

            this.fetchPrompt(); // Fetch the active prompt based on subject, topic, and questionType

        }
        if (prevProps.isOpen != this.props.isOpen && this.props.isOpen && this.props.calledFrom == "") {
            const preselectedCourseId = this.normalizeId(this.props.courseId || this.state.courseId);
            const preselectedSubjectId = this.normalizeId(this.props.preselectedSubject);
            const preselectedTopicId = this.normalizeId(this.props.preselectedTopic);
            const preselectedSubtopicId = this.normalizeId(this.props.preselectedSubtopic);

            //  alert ("AddEditQ inside 2nd this.props.question " + JSON.stringify(this.props.question))
            this.setState({
                question: "",
                options: { A: "", B: "", C: "", D: "" },
                correctAnswer: "",
                steps: "",
                questionTitle: "",
                variationId: "",  // Retain variationId for existing questions
                // In add mode, inherit course from QuestionManager selection.
                courseId: preselectedCourseId,
                subjectId: preselectedSubjectId,
                topicId: preselectedTopicId,
                subtopicId: preselectedSubtopicId,
                selectedSubtopic: preselectedSubtopicId,
                selectedSubtopicName: this.props.preselectedSubtopicName,
                comment: "",
                diagramCode: "",
                diagramFormat: DIAGRAM_FORMATS.NONE,
                diagramScene: null,
                passageSetId: '',
                questionOrderWithinPassage: '',
                passageTitle: '',
                passageText: '',
                passageSkillsText: '',
                questionPurpose: 'exam',
                remediationStage: '',
                targetSubskillsText: '',
                mode: "",
                buttonText: "Save",
                selectedSkills: [],
                isFormDirty: false,
                saveError: '',
                saveSuccess: false,
                isSaving: false,
                isEditMode: false,
                isDiagramModalOpen: false,
                lastSavedQuestionSnapshot: null,
                // Update other fields as necessary
            }, () => {
                if (preselectedCourseId && this.normalizeId(prevState.courseId) !== preselectedCourseId) {
                    this.handleCourseChange(preselectedCourseId);
                } else {
                    // Keep add-mode dropdowns aligned with current left-pane selection context.
                    this.syncSelectionFromIds(preselectedSubjectId, preselectedTopicId, preselectedSubtopicId);
                }
            });

            this.fetchPrompt(); // Fetch the active prompt based on subject, topic, and questionType
            //alert("subtopicId 2" + this.props.preselectedSubtopic)
        }


        //alert("subtopic " + JSON.stringify(this.props.preselectedSubtopic))
        // alert("topics " + JSON.stringify(this.state.topics))
        // alert("topicID " + JSON.stringify(this.state.topicId))
        // alert("subjectID " + JSON.stringify(this.state.subjectId))            





        // Check if preselectedSubject or preselectedTopic props have changed
        if (
            prevProps.preselectedSubject !== this.props.preselectedSubject ||
            prevProps.preselectedTopic !== this.props.preselectedTopic ||
            prevProps.preselectedSubtopic !== this.props.preselectedSubtopic
        ) {
            const preselectedSubjectId = this.normalizeId(this.props.preselectedSubject);
            const preselectedTopicId = this.normalizeId(this.props.preselectedTopic);
            const preselectedSubtopicId = this.normalizeId(this.props.preselectedSubtopic);

            // Keep modal selection in sync when parent updates preselected hierarchy.
            this.setState({
                subjectId: preselectedSubjectId,
                topicId: preselectedTopicId,
                selectedSubtopic: preselectedSubtopicId,
                subtopicId: preselectedSubtopicId
            });
            this.syncSelectionFromIds(preselectedSubjectId, preselectedTopicId, preselectedSubtopicId);
            this.fetchPrompt(); // Fetch the active prompt based on subject, topic, and questionType
        }

        if (prevProps.courseId !== this.props.courseId) {
            const nextCourseId = this.normalizeId(this.props.courseId);
            // Keep modal course in sync with QuestionManager course changes while modal remains mounted.
            if (nextCourseId && nextCourseId !== this.normalizeId(this.state.courseId)) {
                this.handleCourseChange(nextCourseId);
            }
        }



        //  this.fetchPrompt();
        // alert("preselectedSubject " + this.props.preselectedSubject);
        // alert("prevState.subjectId " + prevState.subjectId);

        // alert("preselectedTopic " + this.props.preselectedTopic);
        // alert("prevState.topicId " + prevState.topicId);


        // Fetch prompt or other data when subjectId or topicId changes
        if (
            (prevState.subjectId !== this.state.subjectId || prevState.topicId !== this.state.topicId) &&
            this.state.subjectId && this.state.topicId
        ) {
            // this.handleSubjectChange(this.state.subjectId)
            // this.handleTopicChange(this.state.topicId)

            this.fetchPrompt(); // Fetch the active prompt based on subject, topic, and questionType

        }

        // Fetch prompt only if both subjectId and topicId are selected and they have changed
        // if (
        //     (prevState.subjectId !== this.state.subjectId || prevState.topicId !== this.state.topicId) &&
        //     this.state.subjectId && this.state.topicId
        // ) {
        //     this.fetchPrompt();  // Fetch the active prompt based on subject, topic, and questionType
        // }



    }

    render() {
        //console.log("Rendering component with selectedSkills: ", this.state.selectedSkills);

        //console.log ("this.props.isOpen " + this.props.isOpen);
        //alert(JSON.stringify(this.props))
        const { subject, date, mode } = this.props;
        const predefinedTasks = this.state.predefinedTasks;
        // const linkedTasks = this.props.allTasks.alltasks;
        // //alert (JSON.stringify(linkedTasks))
        // const currentDate = new Date();
        // // Filter for outstanding tasks and sort by due date
        // const filteredTasks = linkedTasks
        //     .filter(task => task.status !== 'done' && task.subject === this.state.subject)
        //     .sort((a, b) => new Date(a.due) - new Date(b.due));

        const selectedQuestion = this.state.questions[this.state.currentQuestionIndex];
        const selectedPassageContext = this.getPassageContextFromQuestion(selectedQuestion);
        // New NSW Reading imports require A-E. Legacy non-passage rows that only
        // have A-D stay uncluttered unless option E already exists.
        const optionKeysForInlineEditor = ORDERED_OPTION_KEYS.filter((key) => (
            key !== 'E' ||
            Boolean(selectedPassageContext?.passageText) ||
            Object.prototype.hasOwnProperty.call(this.state.options || {}, 'E') ||
            Object.prototype.hasOwnProperty.call(selectedQuestion?.options || {}, 'E')
        ));
        const correctAnswerDiagnostics = this.getCorrectAnswerDiagnostics(selectedQuestion);
        const resolvedCorrectAnswer = correctAnswerDiagnostics.resolvedAnswer;
        const questionPurpose = normalizeQuestionPurpose(this.state.questionPurpose || selectedQuestion?.questionPurpose);
        const questionPurposeOption = getQuestionPurposeOption(questionPurpose);
        const purposeBadgeColor = questionPurpose === 'exam' ? 'primary' : (questionPurpose === 'remediation_drill' ? 'info' : 'warning');
        const targetSubskills = this.parseTargetSubskillsFromState();
        const currentDiagramPreview = normalizeQuestionDiagram({
            ...(selectedQuestion || {}),
            diagramFormat: this.state.diagramFormat,
            diagramCode: this.state.diagramCode,
            diagramScene: this.state.diagramScene,
        });
        const canEditDiagramFromModal = !this.props.isReviewer && !currentDiagramPreview.isSvg;
        const isAdminView = typeof window !== 'undefined'
            ? localStorage.getItem('studyapp_role') !== 'student'
            : true;
        // console.log("selectedQuestion112233 " + JSON.stringify(selectedQuestion))
        // alert("selectedQuestion " + JSON.stringify(selectedQuestion))

        return (
            <div>


                {/* <Button outline className="fa fa-pencil fa-lg" onClick={this.toggleModal}>+</Button> */}
                <Modal isOpen={this.props.isOpen} toggle={() => this.props.toggleModalCallback(this.state.isFormDirty)} modalClassName="add-edit-modal">
                    <ModalHeader toggle={() => this.props.toggleModalCallback(this.state.isFormDirty)} >Question</ModalHeader>
                    <ModalBody>
                        <div className="daily-log-form">

                            {/* ++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */}
                            {this.state.isVerifierMode && !this.state.isEditMode ? (
                                <div className="daily-log-form">
                                    <Row>
                                        {/* ── Left Column: Inline-editable question ── */}
                                        <Col md={8}>
                                            {/* Sticky action bar */}
                                            <div style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg-page)', zIndex: 1000, padding: '10px 12px', borderBottom: '1px solid var(--border-default)', borderRadius: '0 0 6px 6px', boxShadow: '0 2px 4px rgba(0,0,0,.06)' }}>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                                                    <Button size="sm" onClick={this.handlePrevious} disabled={this.state.currentQuestionIndex === 0 || this.state.isSaving}>← Prev</Button>
                                                    <Button size="sm" onClick={this.handleNext} disabled={this.state.currentQuestionIndex === this.state.questions.length - 1 || this.state.isSaving}>Next →</Button>
                                                    <Button size="sm" color="secondary" onClick={this.copyCurrentQuestionForReview}>{this.state.copyStatus || 'Copy'}</Button>
                                                    <span style={{ borderLeft: '1px solid var(--border-default)', height: '20px', alignSelf: 'center' }} />
                                                    {!this.props.isReviewer && (
                                                        <Button size="sm" color="primary" onClick={this.handleSaveEdits} disabled={!this.state.isFormDirty || this.state.isSaving}>
                                                            {this.state.isSaving ? 'Saving…' : '💾 Save Changes'}
                                                        </Button>
                                                    )}
                                                    <span style={{ borderLeft: '1px solid var(--border-default)', height: '20px', alignSelf: 'center' }} />
                                                    <Button
                                                        size="sm"
                                                        color="success"
                                                        onClick={this.handleVerifyQuestion}
                                                        disabled={
                                                            (this.state.questions[this.state.currentQuestionIndex]
                                                                ? this.state.questions[this.state.currentQuestionIndex].verified
                                                                : false)
                                                            || this.state.isSaving
                                                            || this.state.isFormDirty
                                                            || !correctAnswerDiagnostics.matchesOption
                                                        }
                                                    >✔ Correct</Button>
                                                    <Button
                                                        size="sm"
                                                        color="danger"
                                                        onClick={this.handleMarkAsIncorrect}
                                                        disabled={this.state.questions[this.state.currentQuestionIndex]?.incorrect || this.state.forceComment || this.state.isSaving || this.state.isFormDirty}
                                                    >✘ Incorrect</Button>
                                                    <Button
                                                        size="sm"
                                                        className="delete-question-button"
                                                        onClick={this.handleDeleteCurrentQuestion}
                                                        disabled={!this.props.onDeleteQuestion || this.state.isSaving || this.state.isFormDirty}
                                                    >Delete</Button>
                                                    {!this.props.isReviewer && (
                                                        <Button
                                                            size="sm"
                                                            color="success"
                                                            onClick={this.handleAccept}
                                                            disabled={
                                                                this.state.questions[this.state.currentQuestionIndex]?.workflow === "accepted"
                                                                || this.state.isSaving
                                                                || this.state.isFormDirty
                                                                || !correctAnswerDiagnostics.matchesOption
                                                            }
                                                        >Accept</Button>
                                                    )}
                                                    {/* Incorrect modal */}
                                                    <Modal isOpen={this.state.isIncorrectModalOpen} toggle={this.toggleIncorrectModal}>
                                                        <ModalHeader toggle={this.toggleIncorrectModal}>Mark as Incorrect</ModalHeader>
                                                        <ModalBody>
                                                            <Form onSubmit={this.handleMarkAsIncorrect}>
                                                                <FormGroup>
                                                                    <Label for="incorrectComment">Comments</Label>
                                                                    <Input type="textarea" name="incorrectComment" id="incorrectComment" value={this.state.incorrectComment} onChange={(e) => this.setState({ incorrectComment: e.target.value })} required />
                                                                </FormGroup>
                                                                <Button type="submit" color="danger">Submit</Button>{' '}
                                                                <Button color="secondary" onClick={this.toggleIncorrectModal}>Cancel</Button>
                                                            </Form>
                                                        </ModalBody>
                                                    </Modal>
                                                </div>
                                                {(this.state.saveError || this.state.isFormDirty) && (
                                                    <div style={{ marginTop: '8px' }}>
                                                        {this.state.saveError && <Alert color="danger" style={{ marginBottom: this.state.isFormDirty ? '6px' : 0 }}>{this.state.saveError}</Alert>}
                                                        {this.state.isFormDirty && <Alert color="warning" style={{ marginBottom: 0, padding: '6px 12px', fontSize: 'var(--text-xs)' }}>Unsaved changes — click Save Changes before verifying or navigating.</Alert>}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Content area */}
                                            <div style={{ marginTop: '16px', color: 'var(--text-primary)' }}>
                                                {this.state.saveSuccess && <Alert color="info">Saved successfully!</Alert>}

                                                {/* ── Metadata strip ── */}
                                                {(() => {
                                                    const q = this.state.questions[this.state.currentQuestionIndex] || {};
                                                    const fmtDate = (d) => {
                                                        if (!d) return '—';
                                                        const p = new Date(d);
                                                        if (isNaN(p.getTime())) return '—';
                                                        return new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(p);
                                                    };
                                                    const getEmail = (u) => !u ? null : (typeof u === 'string' ? u : (u.email || u.username || null));
                                                    const statusLabel = q.workflow === 'accepted' ? 'Accepted' : q.verified ? 'Verified' : q.incorrect ? 'Incorrect' : 'Unverified';
                                                    const statusColor = { Accepted: '#28a745', Verified: '#28a745', Incorrect: '#ffc107', Unverified: '#dc3545' }[statusLabel];
                                                    const createdBy = getEmail(q.createdBy);
                                                    const verifiedBy = getEmail(q.verifiedBy);
                                                    const incorrectBy = getEmail(q.incorrectBy);
                                                    return (
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', alignItems: 'center', padding: '8px 0 10px', marginBottom: '14px', borderBottom: '1px solid #f1f3f5', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: statusColor + '18', border: `1px solid ${statusColor}55`, color: statusColor, borderRadius: 'var(--radius-lg)', padding: '2px 9px', fontWeight: 700, fontSize: '11px' }}>
                                                                ● {statusLabel}
                                                            </span>
                                                            <span>Diff: <strong style={{ color: 'var(--text-primary)' }}>{q.difficulty || '—'}</strong></span>
                                                            <span>Type: <strong style={{ color: 'var(--text-primary)' }}>{questionPurposeOption.label}</strong></span>
                                                            <span style={{ borderLeft: '1px solid var(--border-default)', height: '14px' }} />
                                                            <span>Created: <strong style={{ color: 'var(--text-primary)' }}>{fmtDate(q.createdAt)}</strong>{createdBy ? <span> by <strong style={{ color: '#0069d9' }}>{createdBy}</strong></span> : null}</span>
                                                            {q.updatedAt && <span>Updated: <strong style={{ color: 'var(--text-primary)' }}>{fmtDate(q.updatedAt)}</strong></span>}
                                                            {verifiedBy && <span>Verified by: <strong style={{ color: '#0069d9' }}>{verifiedBy}</strong></span>}
                                                            {incorrectBy && <span>Flagged by: <strong style={{ color: '#856404' }}>{incorrectBy}</strong></span>}
                                                        </div>
                                                    );
                                                })()}

                                                {/* ── Title (inline editable) ── */}
                                                <div style={{ marginBottom: '14px' }}>
                                                    <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px' }}>Question Title</div>
                                                    <Input
                                                        type="text"
                                                        value={this.state.questionTitle || ''}
                                                        onChange={(e) => this.setState({ questionTitle: e.target.value, isFormDirty: true, saveError: '' })}
                                                        style={{ fontWeight: 700, fontSize: 'var(--text-xl)', border: '1px solid transparent', borderRadius: 'var(--radius-sm)', padding: '4px 8px', backgroundColor: 'transparent', width: '100%', transition: 'border-color .15s, background .15s' }}
                                                        onMouseEnter={e => e.target.style.borderColor = 'var(--border-default)'}
                                                        onMouseLeave={e => { if (document.activeElement !== e.target) e.target.style.borderColor = 'transparent'; }}
                                                        onFocus={e => { e.target.style.borderColor = 'var(--border-focus)'; e.target.style.backgroundColor = '#fff'; }}
                                                        onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.backgroundColor = 'transparent'; }}
                                                        placeholder="Question title"
                                                    />
                                                </div>

                                                {/* Purpose badge */}
                                                <Alert color="light" style={{ border: '1px solid #d1d5db', padding: '8px 12px', marginBottom: '12px' }}>
                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <Badge color={purposeBadgeColor}>{questionPurposeOption.label}</Badge>
                                                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{questionPurposeOption.help}</span>
                                                    </div>
                                                    {questionPurpose === 'remediation_drill' && (
                                                        <div style={{ marginTop: '6px', fontSize: 'var(--text-xs)' }}>
                                                            <strong>Drill metadata:</strong>{' '}
                                                            {this.state.remediationStage ? `Stage: ${this.state.remediationStage}. ` : ''}
                                                            {targetSubskills.length ? `Targets: ${targetSubskills.join(', ')}.` : 'No target subskills set.'}
                                                        </div>
                                                    )}
                                                </Alert>

                                                {/* Passage */}
                                                {selectedPassageContext?.passageText && (
                                                    <div style={{ marginBottom: '16px', padding: '14px', background: '#f8fbff', border: '1px solid #dbe4ff', borderRadius: 'var(--radius-md)' }}>
                                                        <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#4f46e5', fontWeight: 'bold', marginBottom: '8px' }}>Shared Passage</div>
                                                        {selectedPassageContext.passageTitle && <h4 style={{ marginBottom: '10px', color: 'var(--text-primary)' }}>{selectedPassageContext.passageTitle}</h4>}
                                                        <pre style={{ fontSize: 'var(--text-sm)', lineHeight: '1.7', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', color: 'var(--text-primary)', margin: 0 }}>{selectedPassageContext.passageText}</pre>
                                                        {Array.isArray(selectedPassageContext.passageSkills) && selectedPassageContext.passageSkills.length > 0 && (
                                                            <div style={{ marginTop: '10px', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}><strong>Passage skills:</strong> {selectedPassageContext.passageSkills.join(', ')}</div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* ── Question Text (inline editable) ── */}
                                                <div style={{ marginBottom: '16px' }}>
                                                    <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px' }}>Question</div>
                                                    <Input
                                                        type="textarea"
                                                        rows={5}
                                                        value={this.state.question || ''}
                                                        onChange={(e) => this.setState({ question: e.target.value, isFormDirty: true, saveError: '' })}
                                                        style={{ fontFamily: 'Courier New, monospace', fontSize: '17px', fontWeight: 'bold', color: 'var(--text-primary)', lineHeight: '1.6', border: '1px solid transparent', borderRadius: 'var(--radius-sm)', padding: '6px 8px', backgroundColor: 'transparent', resize: 'vertical', width: '100%', transition: 'border-color .15s, background .15s' }}
                                                        onMouseEnter={e => e.target.style.borderColor = 'var(--border-default)'}
                                                        onMouseLeave={e => { if (document.activeElement !== e.target) e.target.style.borderColor = 'transparent'; }}
                                                        onFocus={e => { e.target.style.borderColor = 'var(--border-focus)'; e.target.style.backgroundColor = '#fff'; }}
                                                        onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.backgroundColor = 'transparent'; }}
                                                    />
                                                </div>

                                                {/* Diagram previews size from the Excalidraw scene bounds so taller diagrams stay readable in question manager modals. */}
                                                <QuestionDiagram
                                                    diagram={currentDiagramPreview}
                                                    minExcalidrawHeight={320}
                                                    maxExcalidrawHeight={760}
                                                    showLegacyNotice={currentDiagramPreview.isSvg}
                                                />
                                                <div style={{ marginTop: '8px', marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    {canEditDiagramFromModal && (
                                                        <Button color="secondary" size="sm" type="button" onClick={this.handleOpenDiagramEditor} disabled={this.state.isSaving}>
                                                            {currentDiagramPreview.hasDiagram ? 'Edit Diagram' : 'Create Diagram'}
                                                        </Button>
                                                    )}
                                                    {currentDiagramPreview.isExcalidraw && (
                                                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>Use Edit Diagram then Save Changes to persist.</span>
                                                    )}
                                                </div>
                                                {this.state.isDiagramModalOpen && (
                                                    <div style={{ marginBottom: '16px', border: '1px solid #d1d5db', borderRadius: '10px', overflow: 'hidden', background: 'var(--bg-page)' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #e5e7eb', background: 'var(--bg-subtle)' }}>
                                                            <strong>Edit Diagram</strong>
                                                            <Button color="secondary" size="sm" onClick={this.toggleDiagramModal}>Close</Button>
                                                        </div>
                                                        <div style={{ height: '70vh', minHeight: 480 }}>
                                                            <DiagramEditor scene={this.state.diagramScene || createEmptyExcalidrawScene()} onSceneChange={this.handleSaveDiagramFromModal} />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* ── Answer Options (inline editable) ── */}
                                                <div style={{ marginBottom: '16px' }}>
                                                    <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '8px' }}>Answer Options <span style={{ fontSize: '10px', color: 'var(--text-disabled)', fontWeight: 400, textTransform: 'none' }}>— click to edit</span></div>
                                                    {optionKeysForInlineEditor.map(key => (
                                                        <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                                                            <span style={{ fontWeight: 800, fontSize: 'var(--text-sm)', minWidth: '22px', paddingTop: '6px', color: '#495057' }}>{key}:</span>
                                                            <Input
                                                                type="textarea"
                                                                rows={1}
                                                                value={this.state.options[key] || ''}
                                                                onChange={(e) => this.setState({ options: { ...this.state.options, [key]: e.target.value }, isFormDirty: true, saveError: '' })}
                                                                style={{ fontSize: 'var(--text-base)', lineHeight: '1.5', border: '1px solid transparent', borderRadius: 'var(--radius-sm)', padding: '4px 8px', backgroundColor: 'transparent', resize: 'vertical', flex: 1, transition: 'border-color .15s, background .15s' }}
                                                                onMouseEnter={e => e.target.style.borderColor = 'var(--border-default)'}
                                                                onMouseLeave={e => { if (document.activeElement !== e.target) e.target.style.borderColor = 'transparent'; }}
                                                                onFocus={e => { e.target.style.borderColor = 'var(--border-focus)'; e.target.style.backgroundColor = '#fff'; }}
                                                                onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.backgroundColor = 'transparent'; }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* ── Correct Answer + Difficulty (inline) ── */}
                                                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '16px' }}>
                                                    <div>
                                                        <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px' }}>Correct Answer</div>
                                                        <Input
                                                            type="text"
                                                            value={this.state.correctAnswer || ''}
                                                            onChange={(e) => this.setState({ correctAnswer: e.target.value, isFormDirty: true, saveError: '' })}
                                                            style={{ fontSize: 'var(--text-sm)', fontWeight: 600, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', width: 'auto', minWidth: '80px' }}
                                                            placeholder="Option value"
                                                        />
                                                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: '3px' }}>
                                                            Resolved: <em>{correctAnswerDiagnostics.resolvedAnswer || '—'}</em>
                                                        </div>
                                                        {!correctAnswerDiagnostics.matchesOption && (
                                                            <Alert color="danger" style={{ marginTop: '6px', padding: '6px 10px', fontSize: 'var(--text-xs)' }}>
                                                                Answer does not match any option value. Fix before marking correct.
                                                            </Alert>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px' }}>Difficulty</div>
                                                        <select
                                                            value={this.state.difficultyLevel || ''}
                                                            onChange={(e) => this.setState({ difficultyLevel: e.target.value, isFormDirty: true, saveError: '' })}
                                                            className="form-control form-control-sm"
                                                            style={{ width: 'auto' }}
                                                        >
                                                            <option value="">— Select —</option>
                                                            {[1, 2, 3, 4, 5].map(n => <option key={n} value={String(n)}>{n}</option>)}
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* ── Steps (inline editable) ── */}
                                                <div style={{ marginBottom: '16px' }}>
                                                    <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px' }}>Solution Steps</div>
                                                    <Input
                                                        type="textarea"
                                                        rows={4}
                                                        value={this.state.steps || ''}
                                                        onChange={(e) => this.setState({ steps: e.target.value, isFormDirty: true, saveError: '' })}
                                                        style={{ fontFamily: 'Courier New, monospace', fontSize: 'var(--text-sm)', color: 'var(--text-primary)', fontWeight: 'bold', lineHeight: '1.6', background: '#f7f7f7', border: '1px solid transparent', borderRadius: 'var(--radius-sm)', padding: '8px', resize: 'vertical', width: '100%', transition: 'border-color .15s, background .15s' }}
                                                        onMouseEnter={e => e.target.style.borderColor = 'var(--border-default)'}
                                                        onMouseLeave={e => { if (document.activeElement !== e.target) e.target.style.borderColor = 'transparent'; }}
                                                        onFocus={e => { e.target.style.borderColor = 'var(--border-focus)'; e.target.style.backgroundColor = '#fff'; }}
                                                        onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.backgroundColor = '#f7f7f7'; }}
                                                    />
                                                </div>

                                                {/* ── Advanced Panel: Taxonomy / Classification ── */}
                                                <div style={{ marginTop: '8px', paddingTop: '12px', borderTop: '1px solid #f1f3f5' }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        style={{ width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                                        onClick={() => this.setState(prev => ({ isAdvancedPanelOpen: !prev.isAdvancedPanelOpen }))}
                                                    >
                                                        <span>📂 Classification &amp; Taxonomy</span>
                                                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{this.state.isAdvancedPanelOpen ? '▲ collapse' : '▼ expand'}</span>
                                                    </button>

                                                    {this.state.isAdvancedPanelOpen && (
                                                        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '14px', padding: '14px', background: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)' }}>

                                                            {/* Course */}
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                                <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', minWidth: '80px', marginBottom: 0 }}>Course</label>
                                                                <select
                                                                    value={this.state.courseId}
                                                                    onChange={this.handleCourseChange}
                                                                    className="form-control form-control-sm"
                                                                    disabled={this.props.isReviewer}
                                                                    style={{ flex: 1, minWidth: '160px' }}
                                                                >
                                                                    {this.state.courses.map((course) => (
                                                                        <option key={course._id} value={course._id}>{course.name}</option>
                                                                    ))}
                                                                </select>
                                                            </div>

                                                            {/* Subject / Topic / Subtopic */}
                                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                                <div style={{ flex: 1, minWidth: '140px' }}>
                                                                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Subject</div>
                                                                    <select
                                                                        value={this.state.subjectId}
                                                                        onChange={(e) => { this.handleSubjectChange(e.target.value); this.setState({ isFormDirty: true, saveError: '' }); }}
                                                                        className="form-control form-control-sm"
                                                                        disabled={this.props.isReviewer}
                                                                    >
                                                                        {this.state.subjects.map((subject) => (
                                                                            <option key={subject.subjectId} value={subject.subjectId}>{subject.name}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                <div style={{ flex: 1, minWidth: '140px' }}>
                                                                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Topic</div>
                                                                    <select
                                                                        value={this.state.topicId}
                                                                        onChange={(e) => { this.handleTopicChange(e.target.value); this.setState({ isFormDirty: true, saveError: '' }); }}
                                                                        className="form-control form-control-sm"
                                                                        disabled={this.props.isReviewer}
                                                                    >
                                                                        {this.state.topics.map((topic) => (
                                                                            <option key={topic.topicId} value={topic.topicId}>{topic.name}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                <div style={{ flex: 1, minWidth: '140px' }}>
                                                                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Subtopic</div>
                                                                    <select
                                                                        value={this.state.subtopicId}
                                                                        onChange={(e) => { this.handleSubTopicChange(e.target.value); this.setState({ isFormDirty: true, saveError: '' }); }}
                                                                        className="form-control form-control-sm"
                                                                        disabled={this.props.isReviewer}
                                                                    >
                                                                        <option value="">— none —</option>
                                                                        {this.state.topics
                                                                            .find((t) => t.topicId === this.state.topicId)
                                                                            ?.subtopics?.map((st) => (
                                                                                <option key={st.subtopicId} value={st.subtopicId}>{st.name}</option>
                                                                            ))}
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            {/* Question Type */}
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                                <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', minWidth: '80px', marginBottom: 0 }}>Type</label>
                                                                <select
                                                                    value={this.state.questionType}
                                                                    onChange={(e) => this.setState({ questionType: e.target.value, isFormDirty: true, saveError: '' })}
                                                                    className="form-control form-control-sm"
                                                                    disabled={this.props.isReviewer}
                                                                    style={{ flex: 1, minWidth: '160px' }}
                                                                >
                                                                    <option value="multiple-choice">Multiple Choice</option>
                                                                    <option value="true-false">True/False</option>
                                                                    <option value="short-answer">Short Answer</option>
                                                                </select>
                                                            </div>

                                                            {/* Question Purpose + Drill Stage */}
                                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                                <div style={{ flex: 1, minWidth: '160px' }}>
                                                                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Purpose</div>
                                                                    <select
                                                                        value={questionPurpose}
                                                                        onChange={(e) => { this.handlePurposeMetadataChange('questionPurpose', e.target.value); this.setState({ isFormDirty: true, saveError: '' }); }}
                                                                        className="form-control form-control-sm"
                                                                        disabled={this.props.isReviewer}
                                                                    >
                                                                        {QUESTION_PURPOSE_OPTIONS.map((opt) => (
                                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                        ))}
                                                                    </select>
                                                                    <small style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{questionPurposeOption.help}</small>
                                                                </div>
                                                                <div style={{ flex: 1, minWidth: '140px' }}>
                                                                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Drill Stage</div>
                                                                    <Input
                                                                        type="text"
                                                                        value={this.state.remediationStage}
                                                                        onChange={(e) => { this.handlePurposeMetadataChange('remediationStage', e.target.value); this.setState({ isFormDirty: true, saveError: '' }); }}
                                                                        placeholder="e.g. foundational"
                                                                        className="form-control-sm"
                                                                        disabled={questionPurpose !== 'remediation_drill' || this.props.isReviewer}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Target Subskills */}
                                                            <div>
                                                                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Target Subskills <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--text-disabled)' }}>— one per line</span></div>
                                                                <Input
                                                                    type="textarea"
                                                                    rows={3}
                                                                    value={this.state.targetSubskillsText}
                                                                    onChange={(e) => { this.handlePurposeMetadataChange('targetSubskillsText', e.target.value); this.setState({ isFormDirty: true, saveError: '' }); }}
                                                                    placeholder="One subskill per line"
                                                                    disabled={this.props.isReviewer}
                                                                    className="form-control-sm"
                                                                />
                                                            </div>

                                                            {/* Passage fields — only shown when question belongs to a passage */}
                                                            {selectedPassageContext?.passageText && (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '10px', borderTop: '1px solid var(--border-default)' }}>
                                                                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#4f46e5', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Passage</div>
                                                                    <div>
                                                                        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Title</div>
                                                                        <Input
                                                                            type="text"
                                                                            value={this.state.passageTitle}
                                                                            onChange={(e) => { this.handlePassageFieldChange('passageTitle', e.target.value); this.setState({ isFormDirty: true, saveError: '' }); }}
                                                                            disabled={this.props.isReviewer}
                                                                            className="form-control-sm"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Text</div>
                                                                        <Input
                                                                            type="textarea"
                                                                            rows={6}
                                                                            value={this.state.passageText}
                                                                            onChange={(e) => { this.handlePassageFieldChange('passageText', e.target.value); this.setState({ isFormDirty: true, saveError: '' }); }}
                                                                            disabled={this.props.isReviewer}
                                                                            className="form-control-sm"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Skills <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--text-disabled)' }}>— one per line</span></div>
                                                                        <Input
                                                                            type="textarea"
                                                                            rows={3}
                                                                            value={this.state.passageSkillsText}
                                                                            onChange={(e) => { this.handlePassageFieldChange('passageSkillsText', e.target.value); this.setState({ isFormDirty: true, saveError: '' }); }}
                                                                            disabled={this.props.isReviewer}
                                                                            className="form-control-sm"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}

                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>

                                        {/* ── Right Column: Diagram JSON + Comments ── */}
                                        <Col md={4}>
                                            {/* Diagram JSON editor — only when a diagram exists */}
                                            {currentDiagramPreview.hasDiagram && (() => {
                                                const isExcalidraw = currentDiagramPreview.isExcalidraw;
                                                const isSvg = currentDiagramPreview.isSvg;
                                                // Displayed value: use draft if typing, else derive from state
                                                const liveJson = isExcalidraw
                                                    ? (() => { try { return JSON.stringify(this.state.diagramScene, null, 2); } catch(e) { return ''; } })()
                                                    : (this.state.diagramCode || '');
                                                const displayValue = this.state.diagramJsonText !== null ? this.state.diagramJsonText : liveJson;

                                                const handleJsonChange = (e) => {
                                                    this.setState({ diagramJsonText: e.target.value, diagramJsonError: '' });
                                                };

                                                const handleJsonBlur = () => {
                                                    const draft = this.state.diagramJsonText;
                                                    if (draft === null) return; // nothing typed
                                                    if (isExcalidraw) {
                                                        try {
                                                            const parsed = JSON.parse(draft);
                                                            // Accept objects that at least have an elements array
                                                            const scene = parsed && typeof parsed === 'object' ? parsed : {};
                                                            this.setState((prev) => {
                                                                const updatedQuestions = Array.isArray(prev.questions) ? [...prev.questions] : [];
                                                                if (updatedQuestions[prev.currentQuestionIndex]) {
                                                                    updatedQuestions[prev.currentQuestionIndex] = {
                                                                        ...updatedQuestions[prev.currentQuestionIndex],
                                                                        diagramScene: scene,
                                                                        diagramFormat: 'excalidraw',
                                                                    };
                                                                }
                                                                return {
                                                                    diagramScene: scene,
                                                                    diagramFormat: 'excalidraw',
                                                                    questions: updatedQuestions,
                                                                    diagramJsonText: null,  // clear draft — field now shows live
                                                                    diagramJsonError: '',
                                                                    isFormDirty: true,
                                                                    saveError: '',
                                                                };
                                                            });
                                                        } catch (err) {
                                                            this.setState({ diagramJsonError: 'Invalid JSON — fix before navigating away.' });
                                                        }
                                                    } else if (isSvg) {
                                                        // SVG: just store raw string directly
                                                        this.setState((prev) => {
                                                            const updatedQuestions = Array.isArray(prev.questions) ? [...prev.questions] : [];
                                                            if (updatedQuestions[prev.currentQuestionIndex]) {
                                                                updatedQuestions[prev.currentQuestionIndex] = {
                                                                    ...updatedQuestions[prev.currentQuestionIndex],
                                                                    diagramCode: draft,
                                                                };
                                                            }
                                                            return {
                                                                diagramCode: draft,
                                                                questions: updatedQuestions,
                                                                diagramJsonText: null,
                                                                diagramJsonError: '',
                                                                isFormDirty: true,
                                                                saveError: '',
                                                            };
                                                        });
                                                    }
                                                };

                                                return (
                                                    <div style={{ marginTop: '16px', marginBottom: '16px', paddingBottom: '14px', borderBottom: '1px solid var(--border-default)' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                            <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                                                                {isExcalidraw ? 'Diagram JSON (Excalidraw)' : 'Diagram SVG'}
                                                            </span>
                                                            {this.state.diagramJsonText !== null && (
                                                                <span style={{ fontSize: '10px', color: '#856404', fontWeight: 600 }}>editing…</span>
                                                            )}
                                                        </div>
                                                        <textarea
                                                            value={displayValue}
                                                            onChange={handleJsonChange}
                                                            onBlur={handleJsonBlur}
                                                            rows={14}
                                                            spellCheck={false}
                                                            style={{
                                                                width: '100%',
                                                                fontFamily: 'Courier New, monospace',
                                                                fontSize: '11px',
                                                                lineHeight: '1.5',
                                                                color: 'var(--text-primary)',
                                                                background: this.state.diagramJsonText !== null ? '#fff' : '#f8f9fa',
                                                                border: `1px solid ${this.state.diagramJsonError ? '#dc3545' : (this.state.diagramJsonText !== null ? '#80bdff' : '#dee2e6')}`,
                                                                borderRadius: 'var(--radius-sm)',
                                                                padding: '8px',
                                                                resize: 'vertical',
                                                                transition: 'border-color .15s, background .15s',
                                                            }}
                                                            onFocus={() => { if (this.state.diagramJsonText === null) this.setState({ diagramJsonText: liveJson }); }}
                                                        />
                                                        {this.state.diagramJsonError && (
                                                            <div style={{ fontSize: '11px', color: 'var(--color-danger)', marginTop: '3px' }}>{this.state.diagramJsonError}</div>
                                                        )}
                                                        {!this.state.diagramJsonError && this.state.diagramJsonText === null && (
                                                            <div style={{ fontSize: '10px', color: 'var(--text-disabled)', marginTop: '2px' }}>Click to edit · changes applied on blur</div>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            {selectedQuestion && (
                                                <div className="comments-section" style={{ marginTop: '20px', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
                                                    <h4 style={{ color: 'var(--text-primary)', backgroundColor: 'var(--bg-page)', padding: '5px', borderRadius: 'var(--radius-sm)' }}>Comments</h4>
                                                    {selectedQuestion.comments && selectedQuestion.comments.length > 0 ? (
                                                        selectedQuestion.comments
                                                            .slice()
                                                            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                                                            .map(comment => (
                                                                <div key={comment._id} style={{ marginBottom: '10px' }}>
                                                                    {this.state.editingCommentId === comment._id ? (
                                                                        <div style={{ color: 'var(--text-primary)', backgroundColor: 'var(--bg-page)', padding: '5px', borderRadius: 'var(--radius-sm)' }}>
                                                                            <Input type="textarea" value={this.state.editingCommentText} onChange={(e) => this.setState({ editingCommentText: e.target.value })} />
                                                                            <Button size="sm" color="primary" onClick={this.handleSaveEditedComment}>Save</Button>{' '}
                                                                            <Button size="sm" color="secondary" onClick={() => this.setState({ editingCommentId: null, editingCommentText: '' })}>Cancel</Button>
                                                                        </div>
                                                                    ) : (
                                                                        <div style={{ color: 'var(--text-primary)', backgroundColor: 'var(--bg-page)', padding: '5px', borderRadius: 'var(--radius-sm)' }}>
                                                                            <p>{comment.text}</p>
                                                                            <small>By {comment.addedBy && comment.addedBy.email ? comment.addedBy.email : 'Unknown'} on {moment(comment.createdAt).format('DD MMM YYYY, h:mm A')}</small>
                                                                            {(comment.addedBy && comment.addedBy._id === localStorage.getItem('studyapp.com_userID')) && (
                                                                                <div>
                                                                                    <Button size="sm" color="secondary" onClick={() => this.handleEditComment(comment)}>Edit</Button>{' '}
                                                                                    <Button size="sm" color="danger" onClick={() => this.handleDeleteComment(comment)}>Delete</Button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))
                                                    ) : (
                                                        <p>No comments yet.</p>
                                                    )}
                                                    <div className="add-comment" style={{ marginTop: '10px' }}>
                                                        <Input type="textarea" placeholder="Add your comment..." value={this.state.newCommentText} onChange={(e) => this.setState({ newCommentText: e.target.value })} innerRef={this.commentInputRef} style={this.state.forceComment ? { border: '2px solid red' } : {}} />
                                                        <Button color="primary" onClick={this.handleSubmitComment} style={{ marginTop: '5px' }}>Submit Comment</Button>
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>
                                </div>

                            ) : (
                                // Editable fields: if we're NOT in verify mode or if we're in edit mode
                                <div className="edit-display">
                                    <Row>
                                        <Col md={12}>
                                            <div className="main-content">

                                                <LocalForm onSubmit={(values) => this.handleSubmit(values)}>
                                                    {isAdminView && this.state.courseSelectionRequired && (
                                                        <Alert color="warning">
                                                            Admin: select a course to load subjects and prompts.
                                                        </Alert>
                                                    )}
                                                    <Row className='form-group'>
                                                        {/* Course Field */}
                                                        <Label htmlFor="course" md={1}>Course</Label>
                                                        <Col md={3}>
                                                            <select
                                                                id="course"
                                                                value={this.state.courseId}
                                                                onChange={this.handleCourseChange}
                                                                className="form-control"
                                                            >
                                                                {
                                                                    this.state.courses.map((course) => (
                                                                        <option key={course._id} value={course._id}>
                                                                            {course.name}
                                                                        </option>
                                                                    ))}
                                                            </select>
                                                        </Col>

                                                        {/* Subject Dropdown */}
                                                        <Label htmlFor="subject" md={1}>Subject</Label>
                                                        <Col md={3}>
                                                            <select
                                                                id="subject"
                                                                value={this.state.subjectId}
                                                                onChange={(e) => this.handleSubjectChange(e.target.value)}  // Make sure selectedSubject holds the _id
                                                                className="form-control"
                                                            >
                                                                {this.state.subjects.map((subject) => (
                                                                    <option key={subject.subjectId} value={subject.subjectId}>{subject.name}</option> // value should be subjectId
                                                                ))}
                                                            </select>

                                                        </Col>
                                                        {/* Topic Dropdown */}
                                                        <Label htmlFor="topic" md={1}>Topic</Label>
                                                        <Col md={3}>
                                                            <select
                                                                id="topic"
                                                                value={this.state.topicId}
                                                                onChange={(e) => this.handleTopicChange(e.target.value)}  // Make sure selectedTopic holds the _id
                                                                className="form-control"
                                                            >
                                                                {this.state.topics.map((topic) => (
                                                                    <option key={topic.topicId} value={topic.topicId}>{topic.name}</option>  // value should be topicId
                                                                ))}
                                                            </select>

                                                        </Col>
                                                        <Label htmlFor="subtopic" md={2}>Subtopic</Label>
                                                        <Col md={3}>
                                                            <select
                                                                id="subtopic"
                                                                value={this.state.subtopicId}
                                                                onChange={(e) => this.handleSubTopicChange(e.target.value)}
                                                                className="form-control"
                                                            >
                                                                <option value="">Select Subtopic</option>
                                                                {this.state.topics
                                                                    .find((topic) => topic.topicId === this.state.topicId)
                                                                    ?.subtopics?.map((subtopic) => (
                                                                        <option key={subtopic.subtopicId} value={subtopic.subtopicId}>
                                                                            {subtopic.name}
                                                                        </option>
                                                                    ))}
                                                            </select>
                                                        </Col>

                                                    </Row>
                                                    {/* Add this section to display and allow editing of the prompt */}




                                                    {!this.state.isVerifierMode && (
                                                        <Row className='form-group'>

                                                            <Label htmlFor="prompt" md={2}>Prompt</Label>
                                                            <Col md={10}>
                                                                <textarea
                                                                    id="prompt"
                                                                    className="form-control"
                                                                    value={this.state.prompt}  // Controlled component
                                                                    onChange={(e) => this.setState({ prompt: e.target.value })}  // Update the prompt in state
                                                                    rows="6"  // Adjust number of rows as needed
                                                                />
                                                            </Col>

                                                        </Row>

                                                    )}

                                                    {!this.state.isVerifierMode && (
                                                        <Row className='form-group'>
                                                            <Label htmlFor="sampleQuestion" md={2}>Sample Question</Label>
                                                            <Col md={10}>
                                                                <Input
                                                                    type="textarea"
                                                                    id="sampleQuestion"
                                                                    name="sampleQuestion"
                                                                    value={this.state.sampleQuestion}
                                                                    onChange={this.handleSampleQuestionChange}
                                                                    placeholder="Enter sample question"
                                                                    required
                                                                />
                                                            </Col>
                                                        </Row>
                                                    )}

                                                    {!this.state.isVerifierMode && (
                                                        <Row className='form-group'>
                                                            <Label htmlFor="sampleAnswerOptions" md={2}>Sample Question Answer Options</Label>
                                                            <Col md={10}>
                                                                <Input
                                                                    type="textarea"
                                                                    id="sampleAnswerOptions"
                                                                    name="sampleAnswerOptions"
                                                                    value={this.state.sampleAnswerOptions}
                                                                    onChange={this.handleSampleAnsOptionsChange}
                                                                    placeholder="Enter sample question answer options"
                                                                    required
                                                                    rows="4"
                                                                />
                                                            </Col>
                                                        </Row>
                                                    )}


                                                    {!this.state.isVerifierMode && (

                                                        <Row className='form-group'>
                                                            <Label htmlFor="sampleCorrectAnswer" md={2}>Sample Correct Answer</Label>
                                                            <Col md={10}>
                                                                <Input
                                                                    type="textarea"
                                                                    id="sampleCorrectAnswer"
                                                                    name="sampleCorrectAnswer"
                                                                    value={this.state.sampleCorrectAnswer}
                                                                    onChange={this.handleCorrectAnswerChange}
                                                                    placeholder="Enter correct answer"
                                                                    required
                                                                />
                                                            </Col>
                                                        </Row>
                                                    )}


                                                    {!this.state.isVerifierMode && (

                                                        <Row className='form-group'>
                                                            <Label htmlFor="steps" md={2}> Sample Steps</Label>
                                                            <Col md={10}>
                                                                <Input
                                                                    type="textarea"
                                                                    id="sampleSteps"
                                                                    name="samplesteps"
                                                                    value={this.state.samplesteps}
                                                                    onChange={this.handleStepsChange}
                                                                    placeholder="Enter step-by-step explanation"
                                                                    required
                                                                    rows="4"
                                                                />
                                                            </Col>
                                                        </Row>
                                                    )}



                                                    <Row className='form-group'>
                                                        <Label htmlFor="questionType" md={1}>Type</Label>
                                                        <Col md={3}>
                                                            <select
                                                                id="questionType"
                                                                value={this.state.questionType}
                                                                onChange={(e) => this.setState({ questionType: e.target.value })}
                                                                readOnly={this.state.isVerifierMode} // Make read-only in verification mode
                                                                className="form-control"
                                                            >
                                                                <option value="multiple-choice">Multiple Choice</option>
                                                                <option value="true-false">True/False</option>
                                                                <option value="short-answer">Short Answer</option>
                                                            </select>
                                                        </Col>

                                                        <Label htmlFor="difficultyLevel" md={2}>Difficulty Level</Label>

                                                        {this.state.isVerifierMode ? (<Col md={1}>
                                                            <div style={{
                                                                padding: "6px 0",
                                                                fontSize: "var(--text-base)",
                                                                fontWeight: "bold", // Makes the text bold
                                                                color: "var(--text-primary)", // Black color for better contrast
                                                                backgroundColor: "var(--bg-subtle)", // Light background for better readability
                                                                border: "1px solid #ccc", // Optional border to give a box effect
                                                                borderRadius: "var(--radius-sm)", // Smooth border radius
                                                                textAlign: "center", // Center align the text
                                                            }}>
                                                                {this.state.difficultyLevel || "N/A"} {/* Display difficulty level or "N/A" if not set */}
                                                            </div>
                                                        </Col>) : (<Col md={4}>
                                                            <Input
                                                                type="number"
                                                                id="difficultyLevel"
                                                                name="difficultyLevel"
                                                                value={this.state.difficultyLevel}
                                                                onChange={(e) => this.handleDifficultyChange(e.target.value)}
                                                                min={1}
                                                                max={5}
                                                                className="form-control"
                                                            />
                                                        </Col>)}

                                                        {!this.state.isVerifierMode && (
                                                            <Col md={4}>
                                                                {/* The button will be disabled if either selectedTopic or selectedSkills is not set */}
                                                                <Button
                                                                    color="primary"
                                                                    outline
                                                                    onClick={this.fetchQuestionFromOpenAI}
                                                                    //disabled={!this.state.selectedTopic || this.state.selectedSkills.length === 0}  // Disable the button if topic or skills are not selected
                                                                    disabled={!this.state.selectedTopic && !this.state.topicId}  // Disable the button if topic or skills are not selected
                                                                >
                                                                    <span className="fa fa-pencil fa-lg"></span> Fetch Question
                                                                </Button>
                                                            </Col>
                                                        )}
                                                    </Row>

                                                    <Row className='form-group'>
                                                        <Label htmlFor="questionPurpose" md={2}>Question Purpose</Label>
                                                        <Col md={4}>
                                                            <Input
                                                                type="select"
                                                                id="questionPurpose"
                                                                value={questionPurpose}
                                                                onChange={(e) => this.handlePurposeMetadataChange('questionPurpose', e.target.value)}
                                                            >
                                                                {QUESTION_PURPOSE_OPTIONS.map((option) => (
                                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                                ))}
                                                            </Input>
                                                            <small className="text-muted">{questionPurposeOption.help}</small>
                                                        </Col>
                                                        <Label htmlFor="remediationStage" md={2}>Drill Stage</Label>
                                                        <Col md={4}>
                                                            <Input
                                                                type="text"
                                                                id="remediationStage"
                                                                value={this.state.remediationStage}
                                                                onChange={(e) => this.handlePurposeMetadataChange('remediationStage', e.target.value)}
                                                                placeholder="Optional, e.g. foundational, scaffolded, mixed"
                                                                disabled={questionPurpose !== 'remediation_drill'}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <Row className='form-group'>
                                                        <Label htmlFor="targetSubskillsText" md={2}>Target Subskills</Label>
                                                        <Col md={10}>
                                                            <Input
                                                                type="textarea"
                                                                id="targetSubskillsText"
                                                                rows="3"
                                                                value={this.state.targetSubskillsText}
                                                                onChange={(e) => this.handlePurposeMetadataChange('targetSubskillsText', e.target.value)}
                                                                placeholder="One subskill per line. Drill questions are matched to failed exam-style questions through these subskills."
                                                            />
                                                        </Col>
                                                    </Row>

                                                    {!this.state.isVerifierMode && (
                                                        <Row className='form-group'>
                                                            <Label md={2}>Difficulty Adjustment:</Label>
                                                            <Col md={5}>
                                                                <FormGroup check>
                                                                    <Label check>
                                                                        <Input
                                                                            type="radio"
                                                                            name="difficultyAdjustment"
                                                                            value="harder"
                                                                            checked={this.state.difficultyAdjustment === 'harder'}
                                                                            onChange={(e) => {
                                                                                this.setState({ difficultyAdjustment: e.target.value }, () => {
                                                                                    this.fetchPrompt();  // Call fetchPrompt after setting state
                                                                                });
                                                                            }}
                                                                        />{' '}
                                                                        Harder than current
                                                                    </Label>
                                                                </FormGroup>
                                                                <FormGroup check>
                                                                    <Label check>
                                                                        <Input
                                                                            type="radio"
                                                                            name="difficultyAdjustment"
                                                                            value="simpler"
                                                                            checked={this.state.difficultyAdjustment === 'simpler'}
                                                                            onChange={(e) => {
                                                                                this.setState({ difficultyAdjustment: e.target.value }, () => {
                                                                                    this.fetchPrompt();  // Call fetchPrompt after setting state
                                                                                });
                                                                            }}
                                                                        />{' '}
                                                                        Simpler than current
                                                                    </Label>
                                                                </FormGroup>
                                                            </Col>
                                                        </Row>
                                                    )}

                                                    {!this.state.isVerifierMode && (

                                                        <Row className='form-group'>
                                                            <Label htmlFor="generateVariations" md={2}>Generate Variations</Label>
                                                            <Col md={3}>
                                                                <input
                                                                    type="checkbox"
                                                                    id="generateVariations"
                                                                    checked={this.state.generateVariations}
                                                                    onChange={(e) => {
                                                                        this.setState({ generateVariations: e.target.checked }, () => {
                                                                            this.fetchPrompt();  // Update prompt after state change
                                                                        });
                                                                    }}
                                                                />
                                                            </Col>
                                                            <Label htmlFor="diagrams" md={2}>Diagrams?</Label>
                                                            <Col md={3}>
                                                                <input
                                                                    type="checkbox"
                                                                    id="diagrams"
                                                                    checked={this.state.diagrams}
                                                                    onChange={(e) => {
                                                                        this.setState({ diagrams: e.target.checked }, () => {
                                                                            this.fetchPrompt();  // Update prompt after state change
                                                                        });
                                                                    }}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    )}
                                                    {!this.state.isVerifierMode && (
                                                        <Row className="form-group">
                                                            <Label htmlFor="bulkQuestionsJsonInput" md={2}>Paste Bulk Questions JSON</Label>
                                                            <Col md={10}>
                                                                <textarea
                                                                    id="bulkQuestionsJsonInput"
                                                                    className="form-control"
                                                                    value={this.state.bulkQuestionsJsonInput || ''}  // Ensure controlled component
                                                                    onChange={(e) => this.setState({ bulkQuestionsJsonInput: e.target.value, parseError: '' })}  // Update state on input change
                                                                    placeholder="Paste JSON data here"
                                                                    rows="10"
                                                                />
                                                                {this.state.parseError && <p style={{ color: 'red' }}>{this.state.parseError}</p>}
                                                            </Col>
                                                        </Row>
                                                    )}
                                                    {!this.state.isVerifierMode && (
                                                        <Row className="form-group">
                                                            <Col md={10}>
                                                                <Button color="primary" onClick={this.handleBulkSaveFromJson}>Save Bulk Questions</Button>
                                                            </Col>
                                                        </Row>

                                                    )}
                                                    {!this.state.isVerifierMode && (

                                                        <Row className="form-group">
                                                            <Label htmlFor="numQuestions" md={2}>Number of Questions</Label>
                                                            <Col md={2}>
                                                                <input
                                                                    type="number"
                                                                    id="numQuestions"
                                                                    value={this.state.numQuestions}
                                                                    onChange={(e) => {
                                                                        this.setState({ numQuestions: e.target.value }, () => {
                                                                            this.fetchPrompt();  // Call fetchPrompt after setting state
                                                                        });
                                                                    }}
                                                                    className="form-control"
                                                                />
                                                            </Col>
                                                        </Row>

                                                    )}


                                                    <Row>
                                                        <Col md={12}> &nbsp;</Col>
                                                    </Row>

                                                    {selectedPassageContext?.passageText && (
                                                        <>
                                                            <Row className='form-group'>
                                                                <Label htmlFor="passageTitle" md={2}>Passage Title</Label>
                                                                <Col md={10}>
                                                                    <Input
                                                                        type="text"
                                                                        id="passageTitle"
                                                                        value={this.state.passageTitle}
                                                                        onChange={(e) => this.handlePassageFieldChange('passageTitle', e.target.value)}
                                                                    />
                                                                </Col>
                                                            </Row>
                                                            <Row className='form-group'>
                                                                <Label htmlFor="passageText" md={2}>Passage Text</Label>
                                                                <Col md={10}>
                                                                    <Input
                                                                        type="textarea"
                                                                        id="passageText"
                                                                        rows="10"
                                                                        value={this.state.passageText}
                                                                        onChange={(e) => this.handlePassageFieldChange('passageText', e.target.value)}
                                                                    />
                                                                </Col>
                                                            </Row>
                                                            <Row className='form-group'>
                                                                <Label htmlFor="passageSkillsText" md={2}>Passage Skills</Label>
                                                                <Col md={10}>
                                                                    <Input
                                                                        type="textarea"
                                                                        id="passageSkillsText"
                                                                        rows="4"
                                                                        value={this.state.passageSkillsText}
                                                                        onChange={(e) => this.handlePassageFieldChange('passageSkillsText', e.target.value)}
                                                                        placeholder="One skill per line"
                                                                    />
                                                                </Col>
                                                            </Row>
                                                        </>
                                                    )}

                                                    <Row className='form-group'>
                                                        <Col md={12}>
                                                            {this.state.comment && (
                                                                <QuestionAnsComp
                                                                    data={this.state.comment}
                                                                    onChange={this.handleQuestionDataChange}
                                                                    questionTitle1={this.state.questions[this.state.currentQuestionIndex].question_title}
                                                                    isVerifierMode={this.state.isVerifierMode}
                                                                />
                                                            )}
                                                        </Col>
                                                    </Row>

                                                    {/* <Row className='form-group'>
                                                <Label htmlFor="comment" md={12}>Comment</Label>
                                                <Col md={12}>
                                                    <Control.textarea model=".comment" id="comment" name="comment"
                                                        rows="6"
                                                        value={JSON.stringify(this.state.comment)}
                                                        onChange={(event) => this.handleChange(event)}
                                                        className="form-control" />
                                                </Col>
                                            </Row> */}

                                                    <Row className='form-group'>
                                                        <Col md={5}>
                                                            {/* <Button type="submit" color="primary">Submit</Button> */}
                                                            <Button color="primary" outline onClick={this.handleUpdateLog} disabled={this.state.isSaving}>
                                                                <span className="fa fa-pencil fa-lg"></span> {this.state.buttonText}
                                                            </Button>
                                                        </Col>
                                                        <Col md={5}>
                                                            <Button
                                                                color="success"
                                                                onClick={this.handleVerifyQuestion}
                                                                disabled={(this.state.questions[this.state.currentQuestionIndex] ? this.state.questions[this.state.currentQuestionIndex].verified : false) || this.state.isSaving || this.state.isFormDirty} // Disable if already verified or while pending edits still need to be saved.
                                                            >
                                                                Mark as Verified
                                                            </Button>
                                                            {/* **New Button: Mark as Incorrect** */}
                                                            <Button
                                                                color="danger"
                                                                onClick={this.toggleIncorrectModal}
                                                                disabled={(this.state.questions[this.state.currentQuestionIndex] ? this.state.questions[this.state.currentQuestionIndex].incorrect : false) || this.state.isSaving || this.state.isFormDirty} // Disable if already marked incorrect or while edits still need to be saved.
                                                                className="ml-2"
                                                            >
                                                                Mark as Incorrect
                                                            </Button>

                                                        </Col>
                                                        <Col md={2}>
                                                            {!this.props.isReviewer && (
                                                                <Button color="success" onClick={this.handleAccept} disabled={this.state.isSaving || this.state.isFormDirty}>Accept</Button>
                                                            )}
                                                        </Col>

                                                    </Row>
                                                    {/* **New Modal: Mark as Incorrect** */}
                                                    <Modal isOpen={this.state.isIncorrectModalOpen} toggle={this.toggleIncorrectModal}>
                                                        <ModalHeader toggle={this.toggleIncorrectModal}>Mark as Incorrect</ModalHeader>
                                                        <ModalBody>
                                                            <Form onSubmit={this.handleMarkAsIncorrect}>
                                                                <FormGroup>
                                                                    <Label for="incorrectComment">Comments</Label>
                                                                    <Input
                                                                        type="textarea"
                                                                        name="incorrectComment"
                                                                        id="incorrectComment"
                                                                        value={this.state.incorrectComment}
                                                                        onChange={(e) => this.setState({ incorrectComment: e.target.value })}
                                                                        required
                                                                    />
                                                                </FormGroup>
                                                                <Button type="submit" color="danger">Submit</Button>{' '}
                                                                <Button color="secondary" onClick={this.toggleIncorrectModal}>Cancel</Button>
                                                            </Form>
                                                        </ModalBody>
                                                    </Modal>
                                                    {/* **Optional: Display Incorrect Status and Comments** */}
                                                    {this.state.questions[this.state.currentQuestionIndex] && this.state.questions[this.state.currentQuestionIndex].incorrect && (
                                                        <Row className='form-group mt-3'>
                                                            <Col md={12}>
                                                                <div style={{
                                                                    color: 'red',
                                                                    backgroundColor: '#f8d7da',
                                                                    padding: '10px',
                                                                    margin: '10px 0',
                                                                    border: '1px solid #f5c6cb',
                                                                    borderRadius: '5px',
                                                                }}>

                                                                    <strong>Marked as Incorrect</strong> by {this.state.questions[this.state.currentQuestionIndex].incorrectBy ? this.state.questions[this.state.currentQuestionIndex].incorrectBy.email : 'Unknown'}:
                                                                    <p>{selectedQuestion.incorrectComments}</p>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                    <Row className='form-group'>
                                                        <Col md={12}>
                                                            {this.state.saveError && (
                                                                <Alert color="danger">
                                                                    {this.state.saveError}
                                                                </Alert>
                                                            )}
                                                            {/* Render the success message */}
                                                            {this.state.saveSuccess && (
                                                                <div style={{
                                                                    color: 'green',
                                                                    backgroundColor: '#d4edda',
                                                                    padding: '10px',
                                                                    margin: '10px 0',
                                                                    border: '1px solid #c3e6cb',
                                                                    borderRadius: '5px',
                                                                }}>
                                                                    Saved successfully!
                                                                </div>
                                                            )}
                                                        </Col>
                                                    </Row>



                                                    <Row className='form-group'>
                                                        <Col md={6}>
                                                            <Button onClick={this.handlePrevious} disabled={this.state.currentQuestionIndex === 0 || this.state.isSaving}>Previous</Button>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Button onClick={this.handleNext} disabled={this.state.currentQuestionIndex === this.state.questions.length - 1 || this.state.isSaving}>Next</Button>

                                                        </Col>
                                                    </Row>





                                                </LocalForm>


                                            </div>

                                        </Col>
                                    </Row>
                                    {/* Save and Cancel Buttons */}
                                    <Row className="form-group">
                                        <Col md={6}>
                                            <button onClick={this.handleSaveEdits} className="btn btn-success" disabled={this.state.isSaving}>
                                                {this.state.isSaving ? 'Saving...' : 'Save Changes'}
                                            </button>
                                        </Col>
                                        <Col md={6}>
                                            <button onClick={this.handleCancelEdits} className="btn btn-secondary" disabled={this.state.isSaving}>
                                                Discard Changes
                                            </button>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Render Question Details */}



                        </div>


                    </ModalBody>

                </Modal>
            </div >

        );
    }

}
export default connect(mapStateToProps, mapDispatchToProps)(AddEditQuestions); 
